
import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';

// Marketing pages (lazy loaded for code splitting)
const PhilosophyPage = lazy(() => import('./pages/PhilosophyPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ForAgenciesPage = lazy(() => import('./pages/ForAgenciesPage'));
const ForHotelsPage = lazy(() => import('./pages/ForHotelsPage'));
const HotelConciergePage = lazy(() => import('./pages/HotelConciergePage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const JournalPostPage = lazy(() => import('./pages/JournalPostPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const DestinationsIndexPage = lazy(() => import('./pages/DestinationsIndexPage'));
const DestinationGuidePage = lazy(() => import('./pages/DestinationGuidePage'));
const DayPlannerPage = lazy(() => import('./pages/DayPlannerPage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const AskAura = lazy(() => import('./components/AskAura'));
const SharePrivacyModal = lazy(() => import('./components/SharePrivacyModal'));
const LocalEvents = lazy(() => import('./components/LocalEvents'));
import { auth, db, ensureAuthenticated, isTokenNearExpiry, forceTokenRefresh, backupTripToLocalStorage, getUnsavedTripBackup, clearTripBackup, UnsavedTripBackup } from './services/firebase';
import { onAuthStateChanged, onIdTokenChanged, signOut } from 'firebase/auth';
import { collection, addDoc, query, where, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, increment, serverTimestamp } from 'firebase/firestore';

import LandingPage from './components/LandingPage';
const Onboarding = lazy(() => import('./components/Onboarding'));
const DestinationSelector = lazy(() => import('./components/DestinationSelector'));
const DailyItinerary = lazy(() => import('./components/DailyItinerary'));
const Postcard = lazy(() => import('./components/Postcard'));
const LogisticsModule = lazy(() => import('./components/LogisticsModule'));
const ESimModule = lazy(() => import('./components/ESimModule'));
const AtmosphericSidebar = lazy(() => import('./components/AtmosphericSidebar'));
import LoadingScreen from './components/LoadingScreen';
const SocialMemento = lazy(() => import('./components/SocialMemento'));
const SharePreview = lazy(() => import('./components/SharePreview'));
const AuthScreen = lazy(() => import('./components/AuthScreen'));
const PaymentGateway = lazy(() => import('./components/PaymentGateway'));

const AtriumMode = lazy(() => import('./components/AtriumMode')); 
const EmailModal = lazy(() => import('./components/EmailModal')); 
const VeoStudio = lazy(() => import('./components/ReelStudio'));
const AuraStudio = lazy(() => import('./components/AuraStudio'));
const BusinessCard = lazy(() => import('./components/BusinessCard'));
const interactiveMapImport = () => import('./components/InteractiveMap');
const InteractiveMap = lazy(interactiveMapImport);
import AirplaneIcon from './components/AirplaneIcon';
import ErrorBoundary from './components/ErrorBoundary';
import OnboardingTour, { isOnboardingComplete, resetOnboarding } from './components/OnboardingTour';
import { Clock, Sparkles, ArrowUpDown, HelpCircle, Users } from 'lucide-react';
// DnD temporarily disabled — @hello-pangea/dnd causes React error #300 crash
// import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
const Dashboard = lazy(() => import('./components/Dashboard'));
const TemplateGallery = lazy(() => import('./components/TemplateGallery'));
import { UserInput, TripData, DayItinerary, LogisticsData, UserProfile, AtriumItem, Palette, SavedTrip, DossierTemplate, VisualAesthetic, Collaborator, BookingLock, WeatherData } from './types';
import { canEditTrip, isCollaborator, getCollaboratorInfo, acceptCollaboration } from './services/collaborationService';
// Gemini service — dynamically imported to avoid loading 262KB SDK on every page
// Functions are loaded on first use (trip generation, image gen, atrium)
let _gemini: typeof import('./services/geminiService') | null = null;
const getGemini = async () => {
  if (!_gemini) _gemini = await import('./services/geminiService');
  return _gemini;
};
const generateTripSkeletonSplit = async (...args: Parameters<typeof import('./services/geminiService').generateTripSkeletonSplit>) => (await getGemini()).generateTripSkeletonSplit(...args);
const generateSingleDay = async (...args: Parameters<typeof import('./services/geminiService').generateSingleDay>) => (await getGemini()).generateSingleDay(...args);
const generateLocationImage = async (...args: Parameters<typeof import('./services/geminiService').generateLocationImage>) => (await getGemini()).generateLocationImage(...args);
const generateVogueImage = async (...args: Parameters<typeof import('./services/geminiService').generateVogueImage>) => (await getGemini()).generateVogueImage(...args);
const generateSlowSkeleton = async (...args: Parameters<typeof import('./services/geminiService').generateSlowSkeleton>) => (await getGemini()).generateSlowSkeleton(...args);
const generateAtriumSuggestions = async (...args: Parameters<typeof import('./services/geminiService').generateAtriumSuggestions>) => (await getGemini()).generateAtriumSuggestions(...args);
const enrichVenueUrls = async (...args: Parameters<typeof import('./services/geminiService').enrichVenueUrls>) => (await getGemini()).enrichVenueUrls(...args);
const enrichLogisticsUrls = async (...args: Parameters<typeof import('./services/geminiService').enrichLogisticsUrls>) => (await getGemini()).enrichLogisticsUrls(...args);
const fetchLocalEvents = async (...args: Parameters<typeof import('./services/geminiService').fetchLocalEvents>) => (await getGemini()).fetchLocalEvents(...args);
const verifyAndReplaceClosedVenues = async (...args: Parameters<typeof import('./services/geminiService').verifyAndReplaceClosedVenues>) => (await getGemini()).verifyAndReplaceClosedVenues(...args);
import { throttledPromiseAll, resetTripCounter, logTripSummary } from './services/rateLimiter';
import { uploadImageToStorage, uploadTripImages } from './services/storageService';
import { validateAndFixLinks } from './services/linkService';
import { deepValidateLogisticsLinks, deepValidateItineraryLinks } from './services/linkValidationService';
import { trackSignup, trackTripStart, trackTripGenerated, trackTripShared, trackPurchase, trackGenerateFail, consumePendingCheckout } from './services/trackingService';
import { sanitizeUserInput } from './services/sanitizeService';
import { generateEmailHTML, copyEmailToClipboard, generateCalendarExport, generateGoogleCalendarExport } from './services/exportService';
import { resetMetaTags } from './services/seoService';
import { ToastProvider, useToast, setGlobalToast } from './contexts/ToastContext';
import { BrandProvider, useBrand } from './contexts/BrandContext';
const WhiteLabelSettings = lazy(() => import('./components/WhiteLabelSettings'));
const BookingSummary = lazy(() => import('./components/BookingSummary'));
const Journeys = lazy(() => import('./components/Journeys'));
const JourneyPost = lazy(() => import('./components/JourneyPost'));
import { registerServiceWorker, initInstallPrompt, cacheDossierOffline, canInstall, onInstallAvailableChange, promptInstall, isOnline, onConnectivityChange } from './services/pwaService';

// Admin emails that bypass the payment gate — canonical copies live in hooks/useAuthState
import { ADMIN_EMAILS, isAdminEmail } from './hooks/useAuthState';
// Pure app-level helpers extracted from this file (encoding, palettes, template presets)
import { safeEncode, safeDecode, sanitizeForFirestore, PALETTE_DEFINITIONS } from './utils/appHelpers';
import { ensureUserDoc } from './services/firestoreUtils';
import { useModalStates } from './hooks/useModalStates';
import { useVaultSync } from './hooks/useVaultSync';
export { TEMPLATE_PRESETS } from './utils/appHelpers';

const MainApp: React.FC = () => {
  const toast = useToast();
  const { brand, whiteLabelConfig } = useBrand();
  const {
    isWhiteLabelOpen, setIsWhiteLabelOpen,
    showPayment, setShowPayment,
    isUserMenuOpen, setIsUserMenuOpen,
    isMementoOpen, setIsMementoOpen,
    isEmailModalOpen, setIsEmailModalOpen,
    isVeoStudioOpen, setIsVeoStudioOpen,
    isAuraStudioOpen, setIsAuraStudioOpen,
    isShareModalOpen, setIsShareModalOpen,
    calendarMenuOpen, setCalendarMenuOpen,
  } = useModalStates();

  // Register global toast for use outside React (e.g., exportService)
  useEffect(() => {
    setGlobalToast(toast);
  }, [toast]);

  // Reset OG meta tags to defaults when entering the main app (away from shared dossier pages)
  useEffect(() => {
    resetMetaTags();
  }, []);

  // --- AUTH & USER STATE ---
  // Quick auth hint: if we've never logged in, skip the loading spinner entirely
  const hasAuthHint = typeof window !== 'undefined' && localStorage.getItem('aura_auth_hint') === '1';
  const [user, setUser] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(hasAuthHint); // Only show spinner if user was previously logged in
  const [paymentMode, setPaymentMode] = useState<'upgrade' | 'manage'>('upgrade');
  const [showLanding, setShowLanding] = useState(false); // Landing page is handled by Shell in index.tsx

  const navigate = useNavigate();
  const { tripId: urlTripId } = useParams();
  const location = useLocation();

  // If user navigates directly to /vault, show the dashboard
  useEffect(() => {
    if (location.pathname === '/vault' && user) {
      setStep('dashboard');
    }
  }, [location.pathname, user]);

  // Prevent Chrome Auto Dark Mode — declaring "dark light" tells Chrome this page
  // already handles dark mode, so it skips compositor-level color adjustments
  useEffect(() => {
    document.documentElement.style.setProperty('color-scheme', 'dark light', 'important');
  }, []);

  // Added 'atrium_loading' and 'atrium' to the steps
  const [step, setStep] = useState<'onboarding' | 'selecting_dest' | 'atrium_loading' | 'atrium' | 'loading' | 'voyage' | 'dashboard' | 'templates'>('onboarding');
  const [userData, setUserData] = useState<UserInput | null>(null);
  const [tripData, setTripData] = useState<TripData | null>(null);
  const [timeSaved, setTimeSaved] = useState<number | null>(null);
  const [savedTripId, setSavedTripId] = useState<string | null>(null);
  const [isPublicView, setIsPublicView] = useState(false);
  const [voyageError, setVoyageError] = useState<string | null>(null);
  const [headerImage, setHeaderImage] = useState<string | undefined>(undefined);
  
  // Atrium State
  const [atriumItems, setAtriumItems] = useState<AtriumItem[]>([]);
  const [atriumSelections, setAtriumSelections] = useState<AtriumItem[]>([]);
  const [atriumRejections, setAtriumRejections] = useState<AtriumItem[]>([]);

  // Load trip from URL if present — do NOT wait for auth to resolve.
  // Public trips are readable without auth (Firestore rules: isPublic==true).
  // Owner trips will also work because onAuthStateChanged resolves in parallel.
  useEffect(() => {
    if (urlTripId && !hasInitialized.current) {
        hasInitialized.current = true;
        if (!db) {
          // Firebase not initialized — show error immediately instead of permanent spinner
          console.error('[loadPublicTrip] Firebase DB not initialized — cannot load trip');
          setVoyageError('Unable to connect to the travel archives. Please try refreshing.');
          setStep('voyage');
          return;
        }
        loadPublicTrip(urlTripId);
        // Safety timeout: if still on loading screen after 20s, show error
        const safetyTimer = setTimeout(() => {
          if (!tripDataRef.current) {
            setVoyageError('This itinerary is taking too long to load. Please try refreshing.');
            setStep('voyage');
          }
        }, 20000);
        return () => clearTimeout(safetyTimer);
    }
  }, [urlTripId, db]);

  // Map Focus State
  const [activeLocation, setActiveLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Modal States — see useModalStates()

  // Logistics collapse state
  const [showLogistics, setShowLogistics] = useState(true);

  // UI States
  const [isExporting, setIsExporting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shareIsPublic, setShareIsPublic] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [linksValidated, setLinksValidated] = useState(false);
  const isDarkMode = false; // Dark mode removed — light only
  const [loadingMessage, setLoadingMessage] = useState<string>("");
  const [loadingStep, setLoadingStep] = useState<number>(0);

  // Session & Auth Token State
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [showUnsavedTripBanner, setShowUnsavedTripBanner] = useState(false);
  const [unsavedTripBackup, setUnsavedTripBackup] = useState<UnsavedTripBackup | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const isGeneratingRef = useRef(false);
  const authUserRef = useRef<UserProfile | null>(null);
  const userRef = useRef<UserProfile | null>(user);

  // Refs for delayed vault save — avoids stale closure when setTimeout fires 90s later
  const tripDataRef = useRef<TripData | null>(null);
  const headerImageRef = useRef<string | undefined>(undefined);
  const userDataRef = useRef<UserInput | null>(null);

  const hasInitialized = useRef(false);
  const sharePreviewRef = useRef<HTMLDivElement>(null);
  const scrollProgressRef = useRef<HTMLDivElement>(null);

  // Keep userRef in sync with user state (avoids stale closures in async callbacks)
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { tripDataRef.current = tripData; }, [tripData]);
  useEffect(() => { headerImageRef.current = headerImage; }, [headerImage]);
  useEffect(() => { userDataRef.current = userData; }, [userData]);

  // ── LIVE VAULT SYNC — EXACT-COPY GUARANTEE ──────────────────────────────
  // Debounced Firestore mirror of tripData; see hooks/useVaultSync.ts
  useVaultSync(tripData, savedTripId, isPublicView, headerImage);

  // Scroll progress indicator listener (with cleanup)
  useEffect(() => {
    const el = scrollProgressRef.current;
    if (!el || isExporting) return;
    const update = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      el.style.height = `${pct}%`;
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => { window.removeEventListener('scroll', update); };
  }, [isExporting]);

  // --- PRELOAD MAP CHUNK ---
  // When voyage view loads, eagerly fetch InteractiveMap + Leaflet so Expedition Essentials appears instantly
  useEffect(() => {
    if (step === 'voyage') {
      const timer = setTimeout(() => interactiveMapImport(), 500);
      return () => clearTimeout(timer);
    }
  }, [step]);

  // --- SCROLL REVEAL ANIMATION ---
  // Uses MutationObserver to detect new .scroll-reveal elements added dynamically
  // (e.g. Expedition Essentials appearing after weather data loads)
  useEffect(() => {
    // On saved/public trip URLs, skip scroll animations — show content immediately
    const isSavedUrl = isPublicView || window.location.pathname.match(/\/(voyage|trip)\//);
    if (isSavedUrl) {
      const revealAll = () => document.querySelectorAll('.scroll-reveal:not(.revealed)').forEach(el => el.classList.add('revealed'));
      revealAll();
      const mo = new MutationObserver(revealAll);
      mo.observe(document.body, { childList: true, subtree: true });
      return () => mo.disconnect();
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('revealed'); io.unobserve(e.target); } });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    const observeAll = () => {
      document.querySelectorAll('.scroll-reveal:not(.revealed)').forEach(el => io.observe(el));
    };
    observeAll();
    // Re-scan when DOM changes (new sections added dynamically)
    const mo = new MutationObserver(observeAll);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, [step, isPublicView]);

  // --- ONBOARDING TOUR STATE ---
  const [showOnboardingTour, setShowOnboardingTour] = useState(false);

  // --- COLLABORATION STATE ---
  const [isCollaborativeView, setIsCollaborativeView] = useState(false);
  const [collaborationRole, setCollaborationRole] = useState<'owner' | 'editor' | 'viewer'>('owner');
  const [tripOwnerName, setTripOwnerName] = useState<string>('');
  const [tripCollaborators, setTripCollaborators] = useState<Collaborator[]>([]);

  // --- PWA STATE ---
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isAppOnline, setIsAppOnline] = useState(true);

  // --- PWA INITIALIZATION ---
  useEffect(() => {
    // Register service worker
    registerServiceWorker();
    // Listen for install prompt
    initInstallPrompt();
    // Track install availability
    const unsubInstall = onInstallAvailableChange((available) => {
      setShowInstallBanner(available);
    });
    // Track connectivity
    setIsAppOnline(isOnline());
    const unsubConnectivity = onConnectivityChange((online) => {
      setIsAppOnline(online);
    });
    // Check initial install availability after a tick
    setTimeout(() => setShowInstallBanner(canInstall()), 1000);
    return () => {
      unsubInstall();
      unsubConnectivity();
    };
  }, []);

  // --- FIREBASE AUTH LISTENER ---
  useEffect(() => {
    // SIMULATION MODE: If Firebase is not configured, skip listener
    if (!auth || !db) {
        setAuthLoading(false);
        return;
    }

    // Safety valve: never block the UI more than 400ms waiting for Firebase auth.
    // If cached locally (IndexedDB), resolves in ~50-100ms. If not, show login fast.
    // onAuthStateChanged still fires and will update user state when it resolves.
    const authTimeout = setTimeout(() => setAuthLoading(false), 400);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        clearTimeout(authTimeout);
        if (firebaseUser) {
            localStorage.setItem('aura_auth_hint', '1');

            // If handleLogin (from AuthScreen) already set the user profile for this
            // uid, skip the redundant Firestore fetch — it was already read during login.
            // This eliminates a ~5-15s duplicate round-trip on slow connections.
            const currentUser = authUserRef.current;
            if (currentUser && currentUser.id === firebaseUser.uid) {
                // Backstop: if login-time profile creation failed (slow/offline
                // Firestore at sign-in), this idempotent check collects the user
                ensureUserDoc(currentUser);
                setAuthLoading(false);
                return;
            }

            // Fetch extended profile from Firestore (only on page refresh / cold start)
            const docRef = doc(db!, "users", firebaseUser.uid);
            try {
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    setUser(docSnap.data() as UserProfile);
                } else {
                    console.warn("User authenticated but no profile found in Firestore. Creating one...");
                    const newProfile: UserProfile = {
                        id: firebaseUser.uid,
                        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Traveler',
                        email: firebaseUser.email || '',
                        avatarUrl: firebaseUser.photoURL || undefined,
                        hasLicense: false,
                        credits: 1,
                        role: 'user',
                        lastSearchTimestamp: null,
                        createdAt: Date.now()
                    };
                    await setDoc(docRef, sanitizeForFirestore(newProfile));
                    setUser(newProfile);
                }
            } catch (err: any) {
                // The user IS authenticated — queue backend collection with
                // retry no matter which error branch we take below, so a
                // transient Firestore failure can't lose the account
                ensureUserDoc({
                    id: firebaseUser.uid,
                    name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Traveler',
                    email: firebaseUser.email || '',
                    avatarUrl: firebaseUser.photoURL || undefined,
                    hasLicense: false,
                    credits: 1,
                    role: 'user',
                    lastSearchTimestamp: null,
                    createdAt: Date.now(),
                });
                const isOfflineError = err.code === 'unavailable' || (err.message?.includes("offline") && err.code !== 'failed-precondition');

                if (isOfflineError) {
                    console.warn("Aura Traveler is currently offline. Using limited profile.");
                    setUser({
                        id: firebaseUser.uid,
                        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Traveler',
                        email: firebaseUser.email || '',
                        hasLicense: false,
                        credits: 0,
                        lastSearchTimestamp: Date.now()
                    });
                    // Only show offline toast if NOT actively generating a trip (heavy API usage can cause transient Firestore unavailability)
                    if (!isGeneratingRef.current) {
                        toast.warning("You're offline. Some features may be unavailable until connectivity is restored.");
                    }
                } else if (err.code === 'failed-precondition') {
                    console.warn("Firestore persistence issue (multi-tab?). Using Firebase Auth profile.");
                    setUser({
                        id: firebaseUser.uid,
                        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Traveler',
                        email: firebaseUser.email || '',
                        avatarUrl: firebaseUser.photoURL || undefined,
                        hasLicense: false,
                        credits: 1,
                        lastSearchTimestamp: null,
                        createdAt: Date.now()
                    });
                } else {
                    console.error("Firestore Auth Profile Error:", err);
                    // Don't force re-login — user IS authenticated, just Firestore failed
                    setUser({
                        id: firebaseUser.uid,
                        name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Traveler',
                        email: firebaseUser.email || '',
                        avatarUrl: firebaseUser.photoURL || undefined,
                        hasLicense: false,
                        credits: 1,
                        lastSearchTimestamp: null,
                        createdAt: Date.now()
                    });
                }
            }
        } else {
            setUser(null);
            // Don't setShowLanding(true) here — Shell in index.tsx already handles the
            // landing page before App mounts. Setting it here caused users to see a
            // second LandingPage inside App, requiring two clicks to reach AuthScreen.
            localStorage.removeItem('aura_auth_hint');
        }
        setAuthLoading(false);
    });
    return () => { unsubscribe(); clearTimeout(authTimeout); };
  }, []);

  // --- TOKEN REFRESH & SESSION TIMEOUT ---
  useEffect(() => {
    if (!auth) return;

    // Listen for token changes (catches automatic refreshes and forced refreshes)
    const unsubToken = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // If we were showing a session warning, dismiss it
        setShowSessionWarning(false);
      } else if (user && isGeneratingRef.current) {
        // User got logged out DURING trip generation - backup to localStorage
        console.warn('[Auth] User logged out during trip generation! Backing up data...');
        if (tripData && userData) {
          backupTripToLocalStorage({
            tripData,
            userData,
            headerImage,
            savedAt: Date.now(),
            userId: user.id
          });
        }
      }
    });

    // Periodic check for session expiry warning (every 60 seconds)
    const sessionCheckInterval = setInterval(() => {
      if (auth?.currentUser && isTokenNearExpiry()) {
        setShowSessionWarning(true);
      }
    }, 60 * 1000);

    return () => {
      unsubToken();
      clearInterval(sessionCheckInterval);
    };
  }, [user, tripData, userData, headerImage]);

  // --- CHECK FOR UNSAVED TRIP BACKUP ON LOGIN ---
  useEffect(() => {
    if (user && auth?.currentUser) {
      const backup = getUnsavedTripBackup();
      if (backup && backup.userId === user.id) {
        // There is an unsaved trip from a previous session interruption
        setUnsavedTripBackup(backup);
        setShowUnsavedTripBanner(true);
      }
    }
  }, [user]);

  // Update localStorage for simple offline persistance if needed
  // Note: Payment gate is no longer auto-shown on login.
  // It triggers when the user tries to launch the dossier builder with 0 credits.

  // --- BODY BACKGROUND: charcoal for landing, ivory for app ---
  useEffect(() => {
    document.body.style.backgroundColor = showLanding ? '#0A0A0A' : '';
  }, [showLanding]);

  // --- ONBOARDING TOUR TRIGGER ---
  useEffect(() => {
    if (user && !showLanding && step === 'onboarding' && !isOnboardingComplete()) {
      const timer = setTimeout(() => setShowOnboardingTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user, showLanding, step]);

  // STYLING ENGINE: Apply Palette to CSS Variables
  useEffect(() => {
    if (userData && userData.brandingPalette) {
      const palette = PALETTE_DEFINITIONS[userData.brandingPalette];
      if (palette) {
        const root = document.documentElement;
        root.style.setProperty('--color-ivory', palette.ivory);
        root.style.setProperty('--color-charcoal', palette.charcoal);
        root.style.setProperty('--color-gold', palette.gold);
        root.style.setProperty('--color-sand', palette.sand);
      }
    }
  }, [userData]);

  // DARK MODE ENGINE: Apply dark class, CSS variables, and meta theme-color
  // Apply user's selected palette
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark');
    if (userData && userData.brandingPalette) {
      const palette = PALETTE_DEFINITIONS[userData.brandingPalette];
      if (palette) {
        root.style.setProperty('--color-ivory', palette.ivory);
        root.style.setProperty('--color-charcoal', palette.charcoal);
        root.style.setProperty('--color-gold', palette.gold);
        root.style.setProperty('--color-sand', palette.sand);
      }
    }
  }, [userData]);

  // Auth Handlers
  const handleLogin = useCallback((loggedInUser: UserProfile, authInfo?: { isNewUser: boolean; method: string }) => {
      localStorage.setItem('aura_auth_hint', '1');
      authUserRef.current = loggedInUser;
      setUser(loggedInUser);
      setShowLanding(false);
      // sign_up is a key conversion event — returning logins must not inflate it
      if (authInfo?.isNewUser) trackSignup(authInfo.method);
  }, []);

  const handleLogout = useCallback(async () => {
      if (auth) {
        await signOut(auth);
      }
      // Reset white-label brand overrides so next session starts clean
      document.documentElement.style.removeProperty('--color-gold');
      document.documentElement.style.removeProperty('--color-accent');
      setStep('onboarding');
      setUserData(null);
      setTripData(null);
      setIsUserMenuOpen(false);
      setShowLanding(true);
      localStorage.removeItem('aura_auth_hint');
      clearTripBackup();
      // For simulation mode, force clear user
      if (!auth) setUser(null);
  }, []);

  // Session management handlers
  const handleSessionRefresh = async () => {
      const success = await forceTokenRefresh();
      if (success) {
          setShowSessionWarning(false);
      } else {
          setAuthError("Could not refresh your session. Please sign in again.");
      }
  };

  const handleRestoreUnsavedTrip = async () => {
      if (!unsavedTripBackup || !db || !auth?.currentUser) return;

      const token = await ensureAuthenticated();
      if (!token) {
          setAuthError("Please sign in again to restore your itinerary.");
          return;
      }

      // Restore the trip data into the app state
      setTripData({ ...unsavedTripBackup.tripData, itinerary: unsavedTripBackup.tripData?.itinerary || [] });
      setUserData(unsavedTripBackup.userData);
      if (unsavedTripBackup.headerImage) setHeaderImage(unsavedTripBackup.headerImage);

      // Try to save it to Firestore now
      const tripId = await saveToVault(unsavedTripBackup.tripData, unsavedTripBackup.userData);
      if (tripId) {
          clearTripBackup();
          setShowUnsavedTripBanner(false);
          setUnsavedTripBackup(null);
          setStep('voyage');
          navigate(`/voyage/${tripId}`, { replace: true });
      }
  };

  const handleDismissUnsavedTrip = () => {
      clearTripBackup();
      setShowUnsavedTripBanner(false);
      setUnsavedTripBackup(null);
  };

  const handleDismissAuthError = () => {
      setAuthError(null);
  };

  const handlePaymentSuccess = async (credits: number) => {
      if (!user) return;
      // Prefer the real tier/value stashed at begin_checkout over the legacy
      // credits*29 approximation (popup flow never hits the redirect handler).
      const pending = consumePendingCheckout();
      trackPurchase(pending?.value ?? credits * 29, pending?.tier ?? `${credits}-credits`);
      const newCredits = user.credits + credits;
      const updatedUser = { ...user, hasLicense: true, credits: newCredits };
      setUser(updatedUser);
      
      if (db) {
        await updateDoc(doc(db, "users", user.id), { hasLicense: true, credits: newCredits })
          .catch(e => { console.error("Failed to sync license update", e); toast.error("The Concierge couldn\u2019t update your license. Please refresh."); });
      }
      setShowPayment(false);
      toast.success(`${credits} Aura Ticket${credits === 1 ? '' : 's'} added \u2014 you're ready to generate!`);
  };


  const goHome = useCallback(() => {
      // If user has an active itinerary, navigate to dashboard (preserves state) instead of landing
      if (tripData && step === 'voyage') {
        setStep('dashboard');
      } else {
        setShowLanding(true);
      }
  }, [tripData, step]);

  // --- CREDIT SYSTEM & POWER LAW (Rate Limiting) ---
  const consumeCredit = async (): Promise<boolean> => {
    const currentUser = userRef.current;
    if (!currentUser) return false;
    // Admin emails bypass the credit limit
    if (isAdminEmail(currentUser.email)) return true;
    if (currentUser.credits > 0) {
        const newCredits = currentUser.credits - 1;
        const updatedUser = { ...currentUser, credits: newCredits };
        setUser(updatedUser);

        if (db) {
            await updateDoc(doc(db, "users", currentUser.id), { credits: increment(-1) })
              .catch(e => { console.error("Failed to sync credit deduction", e); toast.error("The Concierge couldn\u2019t record your credits. Please refresh."); });
        }

        toast.info(`1 Credit Used. Remaining: ${newCredits}`);
        return true;
    } else {
        toast.warning('Insufficient credits. Please upgrade your plan.', { label: 'Upgrade', onClick: () => { setPaymentMode('upgrade'); setShowPayment(true); } });
        return false;
    }
  };

  const checkRateLimit = (): boolean => {
      if (!user) {
          return false;
      }
      if (!user.lastSearchTimestamp) {
          return true;
      }

      const now = Date.now();
      // 30 second cooldown between generations
      const cooldown = 30 * 1000;
      const timeSinceLastSearch = now - user.lastSearchTimestamp;

      if (db && timeSinceLastSearch < cooldown) {
          const secondsRemaining = Math.ceil((cooldown - timeSinceLastSearch) / 1000);
          toast.addToast({ type: 'warning', message: `Cooldown active. Please wait ${secondsRemaining}s before initiating a new voyage.`, duration: 8000 });
          return false;
      }
      return true;
  };

  // Check for shared URL or payment success on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // Handle Payment Success
    const paymentSuccess = params.get('payment_success');
    if (paymentSuccess === 'true') {
        // Tier/value were stashed at begin_checkout; consuming clears the stash,
        // so the purchase event fires at most once per checkout.
        const pending = consumePendingCheckout();
        if (pending) trackPurchase(pending.value, pending.tier, params.get('session_id') ?? undefined);
        if (user) {
            toast.success('Payment successful! Your credits are being updated.');
            window.history.replaceState({}, document.title, "/");
            return;
        }
    }

    // Handle Payment Canceled
    const paymentCanceled = params.get('payment_canceled');
    if (paymentCanceled === 'true') {
        // Discard the begin_checkout stash so an abandoned checkout can't be
        // mis-attributed to a later, unrelated purchase.
        consumePendingCheckout();
        toast.info('Payment was canceled. You can try again when ready.');
        window.history.replaceState({}, document.title, "/");
        return;
    }

    if (hasInitialized.current) return;
    if (!user || !user.hasLicense) return; 

    const shareData = params.get('data') || params.get('share');

    if (shareData && step === 'onboarding') {
      try {
        const decoded = safeDecode(shareData);
        if (decoded.input && decoded.seed) {
           setShowLanding(false);
           hasInitialized.current = true;
           setUserData(decoded.input);
           performVoyageGeneration(decoded.input, decoded.seed, true);
        }
      } catch (e) {
        console.error("Invalid share link", e);
      }
    }
  }, [user]); 

  const tripImages = useMemo((): string[] => {
      if (!headerImage && !tripData) return [];
      const images: string[] = [];
      if (headerImage) images.push(headerImage);
      if (tripData?.logistics?.hotel?.imageUrl) images.push(tripData.logistics.hotel.imageUrl);
      if (tripData?.itinerary) {
          tripData.itinerary.forEach(day => {
              if (day.imageUrl) images.push(day.imageUrl);
          });
      }
      return images;
  }, [headerImage, tripData]);

  const startAtriumPhase = async (data: UserInput) => {
      // CREDIT / PAYMENT GATE — check before any generation begins
      if (user) {
          const isAdmin = isAdminEmail(user.email);
          if (!isAdmin && !user.hasLicense && user.credits <= 0) {
              setPaymentMode('upgrade');
              setShowPayment(true);
              return;
          }
      }

      // RATE LIMIT CHECK
      if (!checkRateLimit()) {
          return;
      }

      setStep('atrium_loading');
      try {
          const items = await generateAtriumSuggestions(data);
          setAtriumItems(items);
          setStep('atrium');
      } catch (e) {
          console.error("Atrium Generation Failed", e);
          // Reset step so user isn't stuck on loading spinner, then try direct synthesis
          setStep('loading');
          toast.warning('Curating your experience directly...');
          performVoyageGeneration(data);
      }
  };

  const handleAtriumComplete = (selected: AtriumItem[], rejected: AtriumItem[]) => {
      setAtriumSelections(selected);
      setAtriumRejections(rejected);
      if (userData) {
          const updatedData = { ...userData, atriumSelections: selected };
          setUserData(updatedData);
          performVoyageGeneration(updatedData);
      }
  };

  const loadPublicTrip = async (id: string) => {
    if (!db) return;
    setShowLanding(false);
    setStep('loading');
    try {
        const docRef = doc(db, "itineraries", id);
        // Hard 15s timeout — if Firestore is slow or offline, don't hang forever
        const firestoreTimeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Firestore read timed out')), 15000));
        const docSnap = await Promise.race([getDoc(docRef), firestoreTimeout]);
        
        if (docSnap.exists()) {
            const data = docSnap.data() as SavedTrip;
            // If Firestore returned the document, the user has read access
            // (Firestore rules enforce isPublic==true OR authenticated owner).
            // No additional app-level gate needed — having the link IS authorization.
            console.log('[loadPublicTrip]', id, '— fields:', Object.keys(data).join(', '), '| itinerary:', data.itinerary?.length ?? 0, 'days | isPublic:', data.isPublic, '| _placeholder:', (data as any)._placeholder);

            // Filter out placeholder days — only show real generated content
            const realItinerary = (data.itinerary || []).filter((d: any) => !d.isPlaceholder);
            const hasRealContent = realItinerary.length > 0;

            // If no real content exists (all placeholder days), check if trip is stale
            if (!hasRealContent) {
              // Detect stale trips: created > 5 min ago with no real content = generation failed or pre-fix trip
              const rawCreatedAt = data.createdAt as any;
              const createdAt = rawCreatedAt instanceof Date ? rawCreatedAt.getTime()
                : typeof rawCreatedAt === 'number' ? rawCreatedAt
                : rawCreatedAt?.toMillis?.() || 0;
              // If createdAt is unknown (0), assume stale — a trip with no content and no timestamp is definitely old
              const isStale = createdAt === 0 || (Date.now() - createdAt) > 5 * 60 * 1000;

              setTripData({ ...data, itinerary: [], _isStale: isStale } as any);
              const staleInput = data.userInput || {} as any;
              if (staleInput.includeLogistics === undefined) staleInput.includeLogistics = true;
              if (staleInput.includeBudget === undefined) staleInput.includeBudget = true;
              setUserData(staleInput);
              setSavedTripId(id);
              setIsPublicView(true);
              setStep('voyage');
              // Set hero image — use saved URL, or Unsplash fallback for the destination
              if (data.headerImageUrl) {
                setHeaderImage(data.headerImageUrl);
              }
              // Only poll for fresh trips (not stale ones — those will never get content)
              if (!isStale) {
                const pollInterval = setInterval(async () => {
                  try {
                    const freshSnap = await getDoc(doc(db, "itineraries", id));
                    if (freshSnap.exists()) {
                      const freshData = freshSnap.data() as SavedTrip;
                      const freshReal = (freshData.itinerary || []).filter((d: any) => !d.isPlaceholder);
                      if (freshReal.length > 0) {
                        clearInterval(pollInterval);
                        setTripData({ ...freshData, itinerary: freshReal });
                        const freshInput = freshData.userInput || {} as any;
                        if (freshInput.includeLogistics === undefined) freshInput.includeLogistics = true;
                        if (freshInput.includeBudget === undefined) freshInput.includeBudget = true;
                        setUserData(freshInput);
                        if (freshData.headerImageUrl) setHeaderImage(freshData.headerImageUrl);
                      }
                    }
                  } catch { /* silent retry */ }
                }, 5000);
                setTimeout(() => clearInterval(pollInterval), 180000);
              }
              return;
            }

            // Real content exists — render it directly (NO re-generation)
            setTripData({ ...data, itinerary: realItinerary });
            // Default includeLogistics/includeBudget to true for loaded trips
            // (older trips may not have these fields in userInput)
            const loadedInput = data.userInput || {} as any;
            if (loadedInput.includeLogistics === undefined) loadedInput.includeLogistics = true;
            if (loadedInput.includeBudget === undefined) loadedInput.includeBudget = true;
            setUserData(loadedInput);
            setSavedTripId(id);
            setIsPublicView(true);
            // Mark links as validated so weather section doesn't show infinite spinner
            setLinksValidated(true);
            setStep('voyage');

            // Use saved header image or Unsplash fallback
            if (data.headerImageUrl) {
                setHeaderImage(data.headerImageUrl);
            } else if (data.userInput?.destination) {
               // Show Unsplash fallback immediately
               setHeaderImage(`https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=1600&auto=format&fit=crop`);
               // Only generate AI header if the user is the trip owner (has API key + auth)
               if (auth?.currentUser && data.userId === auth.currentUser.uid) {
                 generateVogueImage(data.userInput.destination, "iconic landmark", data.userInput.visualAesthetic, data.userInput.destination)
                   .then(async (img) => {
                     if (!img) return;
                     // Upload base64 → Storage so the URL fits in Firestore (raw base64 can exceed 1MB doc limit)
                     let publicUrl = img;
                     if (img.startsWith('data:image') && id) {
                       const uploaded = await uploadImageToStorage(img, `users/${auth.currentUser!.uid}/itineraries/${id}/header.png`);
                       if (uploaded) publicUrl = uploaded;
                     }
                     setHeaderImage(publicUrl);
                     if (id && db) {
                       updateDoc(doc(db, "itineraries", id), { headerImageUrl: publicUrl }).catch(() => {});
                     }
                   }).catch(() => {});
               }
            }

            // Self-heal booking links: older saved trips may carry hallucinated
            // deep paths on real venue domains (e.g. morsemuseum.org/visit/…)
            // that predate post-enrichment validation. On owner visits, deep
            // HTTP-validate every activity link and persist any fixes so all
            // future viewers of the shared link get working URLs.
            if (auth?.currentUser && data.userId === auth.currentUser.uid && realItinerary.length > 0) {
              const destinationForLinks = data.userInput?.destination || '';
              deepValidateItineraryLinks(realItinerary, destinationForLinks).then(async (fixedDays) => {
                const changed = fixedDays.some((fd, i) => {
                  const orig = realItinerary[i];
                  return ['morning', 'afternoon', 'evening', 'contingency'].some(
                    (k) => (fd as any)[k]?.bookingUrl !== (orig as any)?.[k]?.bookingUrl
                  );
                });
                if (!changed) return;
                setTripData(prev => prev ? {
                  ...prev,
                  itinerary: (prev.itinerary || []).map(d => fixedDays.find(f => f.day === d.day) || d),
                } : prev);
                try {
                  await setDoc(doc(db, "itineraries", id), sanitizeForFirestore({ itinerary: fixedDays }), { merge: true });
                  console.log('[LinkValidation] Self-healed booking links persisted for saved trip');
                } catch { /* non-blocking */ }
              }).catch(() => {});
            }

            // Self-heal day images: for trips where the image-upload phase never completed,
            // regenerate any empty day.imageUrl on owner visits and persist via Storage.
            // Public (non-owner) visitors keep seeing the gradient placeholder until owner heals.
            if (auth?.currentUser && data.userId === auth.currentUser.uid && Array.isArray(data.itinerary)) {
              const ownerUid = auth.currentUser.uid;
              const destination = data.userInput?.destination;
              const aesthetic = data.userInput?.visualAesthetic;
              const missingDays = data.itinerary
                .map((d: any, i: number) => ({ d, i }))
                .filter(({ d }: any) => !d?.imageUrl || d.imageUrl === '');
              if (missingDays.length > 0 && destination) {
                missingDays.forEach(({ d, i }: any) => {
                  const dayNum = d.day ?? i + 1;
                  // Pick the most-vivid prompt for the day — morning activity location > moodTitle > destination
                  const subject = d.morning?.location || d.afternoon?.location || d.moodTitle || `Day ${dayNum}`;
                  generateVogueImage(subject, d.moodTitle || 'travel scene', aesthetic, destination)
                    .then(async (img) => {
                      if (!img) return;
                      let publicUrl = img;
                      if (img.startsWith('data:image')) {
                        const uploaded = await uploadImageToStorage(img, `users/${ownerUid}/itineraries/${id}/day${dayNum}.png`);
                        if (uploaded) publicUrl = uploaded;
                      }
                      // Update local state so the day card shows the image immediately
                      setTripData(prev => prev ? {
                        ...prev,
                        itinerary: (prev.itinerary || []).map((day: any) => day.day === dayNum ? { ...day, imageUrl: publicUrl } : day)
                      } : prev);
                      // Persist back to Firestore — read latest array to avoid clobbering concurrent updates
                      if (id && db) {
                        try {
                          const docRef = doc(db, "itineraries", id);
                          const snap = await getDoc(docRef);
                          if (snap.exists()) {
                            const current = snap.data().itinerary || [];
                            const updated = current.map((day: any) => day.day === dayNum ? { ...day, imageUrl: publicUrl } : day);
                            await updateDoc(docRef, { itinerary: updated });
                          }
                        } catch { /* non-blocking */ }
                      }
                    }).catch(() => {});
                });
              }
            }

                // If weather is missing, set fallback immediately so the section renders
                // instead of showing an infinite loading spinner
                if (!data.weather) {
                  const fallbackWeather = { temperatureRange: 'N/A', condition: 'Check locally', description: `Check local weather for ${data.userInput?.destination || 'your destination'}`, location: data.userInput?.destination || '' } as WeatherData;
                  setTripData(prev => prev ? {
                    ...prev,
                    weather: fallbackWeather,
                    packingList: prev.packingList || [],
                  } : prev);
                }
        } else {
            setVoyageError('This itinerary has vanished from the archives.');
            setStep('voyage');
        }
    } catch (error: any) {
        console.error("[loadPublicTrip] Error loading trip:", id, error?.code, error?.message);
        if (error.message?.includes("offline") || error.message?.includes('timed out')) {
            setVoyageError('Network error: The archives are unreachable. Please try refreshing.');
        } else if (error?.code === 'permission-denied') {
            setVoyageError('This itinerary is private.');
        } else {
            setVoyageError('Could not load this itinerary. Please try refreshing.');
        }
        setStep('voyage');
    }
  };

  const saveToVault = async (itinerary: TripData, input: UserInput) => {
    if (!db || !auth?.currentUser) {
        toast.warning('You must be signed in to save to the vault.');
        return null;
    }

    // Ensure auth token is valid before writing
    const token = await ensureAuthenticated();
    if (!token) {
        setAuthError("Your session has expired. Please sign in again to save your itinerary.");
        // Backup trip data to localStorage so it's not lost
        backupTripToLocalStorage({
            tripData: itinerary,
            userData: input,
            headerImage,
            savedAt: Date.now(),
            userId: user?.id || auth.currentUser?.uid || 'unknown'
        });
        return null;
    }

    setIsSaving(true);
    try {
        // Build the data payload
        const rawData = sanitizeForFirestore({
            ...itinerary,
            headerImageUrl: headerImage,
            userInput: input,
            userId: auth.currentUser.uid,
            ownerName: auth.currentUser.displayName || user?.name || '',
            ownerEmail: auth.currentUser.email || user?.email || '',
            isPublic: true,
            updatedAt: serverTimestamp(),
            collaborators: [],
            collaboratorEmails: [],
        });

        // If we already have a savedTripId, update the existing doc instead of creating a new one
        const isUpdate = !!savedTripId;
        let tripId: string;
        let docRef: any;

        if (isUpdate) {
            tripId = savedTripId!;
            docRef = doc(db, "itineraries", tripId);
        } else {
            // Create a placeholder doc first to get the ID
            rawData.createdAt = serverTimestamp();
            const newDocRef = await addDoc(collection(db, "itineraries"), { _placeholder: true, isPublic: true, userId: auth.currentUser.uid, createdAt: serverTimestamp() });
            tripId = newDocRef.id;
            docRef = newDocRef;
        }

        // Upload all base64 images to Storage and replace with URLs
        let cleanData;
        try {
            // Timeout the entire upload+save flow at 45s to prevent infinite spinner
            const saveTimeout = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Save timed out — please check your connection and try again.')), 45000)
            );

            toast.info('Saving — uploading images…');
            cleanData = await Promise.race([
                uploadTripImages(tripId, rawData, auth.currentUser.uid),
                saveTimeout
            ]);

            // Write the full data — use merge to preserve weather/packingList from slow skeleton
            await Promise.race([
                setDoc(docRef, sanitizeForFirestore(cleanData), { merge: true }),
                saveTimeout
            ]);
        } catch (uploadError) {
            // Delete orphaned placeholder doc on failure (only for new docs)
            if (!isUpdate) { try { await deleteDoc(docRef); } catch {} }
            throw uploadError;
        }

        setSavedTripId(tripId);

        // Update local state with the cleaned URLs so UI uses them going forward
        if (cleanData.headerImageUrl && cleanData.headerImageUrl !== headerImage) {
            setHeaderImage(cleanData.headerImageUrl);
        }
        if (cleanData.itinerary) {
            // Only update image URLs from cleanData — don't overwrite weather/packingList/logistics
            setTripData(prev => {
                if (!prev) return null;
                const mergedItinerary = (cleanData.itinerary || []).map((cd: any) => {
                    const existing = (prev.itinerary || []).find((d: any) => d.day === cd.day);
                    return { ...existing, ...cd, imageUrl: cd.imageUrl || existing?.imageUrl };
                });
                return {
                    ...prev,
                    itinerary: mergedItinerary,
                    headerImageUrl: cleanData.headerImageUrl || prev.headerImageUrl,
                };
            });
        }

        toast.success(isUpdate ? 'Itinerary updated' : 'Itinerary added to vault');

        // Confetti burst on first save (one-time celebration)
        if (!localStorage.getItem('aura_first_save')) {
          localStorage.setItem('aura_first_save', '1');
          const colors = ['#D4AF37', '#8A6D3B', '#E5E0D8', '#FAF9F6', '#C5A036'];
          const container = document.createElement('div');
          container.className = 'confetti-container';
          for (let i = 0; i < 24; i++) {
            const p = document.createElement('div');
            p.className = 'confetti-particle';
            p.style.background = colors[i % colors.length];
            p.style.left = `${(Math.random() - 0.5) * 300}px`;
            p.style.top = `${(Math.random() - 0.5) * 200}px`;
            p.style.animationDelay = `${Math.random() * 0.3}s`;
            container.appendChild(p);
          }
          document.body.appendChild(container);
          setTimeout(() => container.remove(), 2000);
        }

        // Cache dossier for offline viewing (PWA)
        cacheDossierOffline(tripId, cleanData);

        return tripId;
    } catch (error: any) {
        console.error("Error saving to vault:", error);
        const errorMsg = error?.message || '';
        const errorCode = error?.code || '';

        if (errorCode === 'permission-denied' || errorCode === 'unauthenticated' || errorMsg.includes('Missing or insufficient permissions')) {
            setAuthError("Your session has expired. Your itinerary has been saved locally and will be restored when you sign in again.");
            backupTripToLocalStorage({ tripData: itinerary, userData: input, headerImage, savedAt: Date.now(), userId: user?.id || 'unknown' });
        } else if (errorMsg.includes('timeout') || errorMsg.includes('deadline')) {
            toast.warning('Save is taking longer than usual. Your itinerary is safe — try saving manually.');
            backupTripToLocalStorage({ tripData: itinerary, userData: input, headerImage, savedAt: Date.now(), userId: user?.id || 'unknown' });
        } else if (errorCode === 'resource-exhausted' || errorMsg.includes('quota')) {
            toast.error('Storage is full. Please delete an old itinerary and try again.');
        } else if (errorCode === 'invalid-argument') {
            toast.error('Unable to save — the itinerary data has an issue. Please try regenerating.');
        } else {
            toast.error('Unable to save to vault. Your itinerary is backed up locally.');
            backupTripToLocalStorage({ tripData: itinerary, userData: input, headerImage, savedAt: Date.now(), userId: user?.id || 'unknown' });
        }
        return null;
    } finally {
        setIsSaving(false);
    }
  };

  const performVoyageGeneration = async (rawData: UserInput, seed?: number, bypassRateLimit = false) => {
    // Guard against concurrent generation — prevents double-credit-deduct and state corruption
    if (isGeneratingRef.current) {
      console.warn('[performVoyageGeneration] Already in progress — ignoring duplicate call');
      return;
    }
    // Sanitize all user input before passing to AI services
    const data = sanitizeUserInput(rawData);
    // Local flag (not React state) — the catch block below must distinguish a
    // true generation failure from a post-display save error, and the `step`
    // state captured by this closure goes stale once setStep('voyage') runs.
    let voyageDisplayed = false;

    // PORTFOLIO VENUES: concierge teams with registered sister venues (set up
    // in the Concierge Studio) get them woven into full multi-day itineraries.
    // Delivered via agentFavorites, which flows through the skeleton and
    // per-day prompts. The directive tells the model to feature these venues
    // only when they're in/near the destination and genuinely fit — a Tavistock
    // property's Orlando venues must never appear in a Paris itinerary.
    const portfolioVenues = whiteLabelConfig?.partnerVenues;
    if (portfolioVenues?.length && !data.agentFavorites?.includes('PORTFOLIO PRIORITY:')) {
        const propertyLabel = whiteLabelConfig?.hotelName || whiteLabelConfig?.agencyName || 'the property';
        const venueList = portfolioVenues.slice(0, 8).map(v => `${v.name} (${v.category})`).join('; ');
        const directive = `PORTFOLIO PRIORITY: ${propertyLabel} belongs to a hospitality group whose own venues include: ${venueList}. If any of these are located in or near ${data.destination} and genuinely fit the traveler's vibe and budget, feature them in the itinerary using their exact names and note they are part of the hotel's collection. Never force one where it doesn't fit, never place a venue in the wrong city, never invent additional portfolio venues.`;
        data.agentFavorites = data.agentFavorites ? `${data.agentFavorites}. ${directive}` : directive;
    }

    // Mark that we are in a long-running generation (for auth state change detection)
    isGeneratingRef.current = true;
    setAuthError(null);
    // 1. API KEY GUARD: If using premium image models, check for API key
    const isPremiumAesthetic = 
        data.visualAesthetic === VisualAesthetic.TheHotelCollection || 
        data.visualAesthetic === VisualAesthetic.EditorialVogue || 
        data.visualAesthetic === VisualAesthetic.HighEndPhotography;

    if (isPremiumAesthetic) {
        const aistudio = (window as any).aistudio;
        const hasKey = aistudio ? await aistudio.hasSelectedApiKey() : true;
        if (!hasKey && aistudio) {
            toast.warning(
                `The '${data.visualAesthetic}' aesthetic works best with a paid Gemini API key.`,
                { label: 'Select Key', onClick: () => aistudio.openSelectKey() }
            );
        }
    }

    // 2. CREDIT GUARD: Check if user has credits (admins bypass)
    if (!bypassRateLimit && user) {
        const isAdmin = ADMIN_EMAILS.includes(user.email?.toLowerCase());
        if (!isAdmin && !user.hasLicense && user.credits <= 0) {
            toast.warning('You\'re out of Aura Tickets! Please top up to continue.', { label: 'Top Up', onClick: () => { setPaymentMode('upgrade'); setShowPayment(true); } });
            setPaymentMode('upgrade');
            setShowPayment(true);
            return;
        }
    }

    // Double check rate limit if not bypassed (e.g. shared link)
    if (!bypassRateLimit && !checkRateLimit()) return;

    setStep('loading');
    setLoadingStep(0);
    setLoadingMessage("Architecting your bespoke itinerary...");

    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    const duration = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    // Start header image generation in parallel with skeleton
    const headerImagePromise = generateVogueImage(data.destination, "iconic landmark", data.visualAesthetic, data.destination);
    
    // Update UI immediately when header image is ready
    headerImagePromise.then((img) => {
        if (img) {
            setHeaderImage(img);
            setTripData(prev => prev ? { ...prev, headerImageUrl: img } : null);
        }
    }).catch(err => { console.warn('Header image failed:', err); });

    try {
      // Reset API call counter for this trip generation
      resetTripCounter();

      // 1. Generate skeleton in TWO PHASES for speed:
      //    - Fast skeleton (4-5s): day summaries, welcome note, budget → enough to start day generation
      //    - Slow skeleton (10-14s): logistics, weather, packing → merges in background
      setLoadingStep(1);
      setLoadingMessage("Consulting the archives for " + data.destination + "...");
      trackTripStart(data.destination);
      const generationStartTime = Date.now();
      const { fast: fastSkeleton, slowPromise } = await generateTripSkeletonSplit(data, seed);
      setLoadingStep(2);

      // Start with fast skeleton — days can begin generating immediately
      const skeleton: Partial<any> = { ...fastSkeleton };

      // Merge slow skeleton when it arrives (logistics, weather, etc.)
      // This runs in background — day generation doesn't wait for it
      slowPromise.then(slow => {
          const mergedLogistics = slow.logistics ? validateAndFixLinks(
            slow.logistics, data.destination,
            { startDate: data.startDate, endDate: data.endDate }
          ) : undefined;

          // Add SVG placeholders for flight/hotel images
          const makeSvg = (name: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"><rect width="100%" height="100%" fill="#1a1a1a"/><text x="50%" y="50%" font-family="serif" font-size="32" fill="#C5A059" text-anchor="middle" dominant-baseline="middle">${name}</text></svg>`)}`;
          if (mergedLogistics?.flight && !mergedLogistics.flight.imageUrl) mergedLogistics.flight.imageUrl = makeSvg(mergedLogistics.flight.name || 'Flight');
          if (mergedLogistics?.hotel && !mergedLogistics.hotel.imageUrl) mergedLogistics.hotel.imageUrl = makeSvg(mergedLogistics.hotel.name || 'Hotel');

          // Merge into skeleton for later use
          Object.assign(skeleton, slow);
          if (mergedLogistics) skeleton.logistics = mergedLogistics;

          // Update UI with logistics, weather, packing list
          setTripData(prev => prev ? {
            ...prev,
            logistics: mergedLogistics || prev.logistics,
            weather: slow.weather || prev.weather,
            packingList: slow.packingList || prev.packingList,
            destinationInsights: slow.destinationInsights || prev.destinationInsights,
          } : prev);

          // Now enrich + validate logistics links
          if (mergedLogistics) {
            enrichLogisticsUrls(mergedLogistics, data.destination).then(enriched => {
              setTripData(prev => prev ? { ...prev, logistics: enriched } : null);
              return deepValidateLogisticsLinks(enriched, data.destination);
            }).then(validated => {
              setTripData(prev => prev ? { ...prev, logistics: validated } : null);
              setLinksValidated(true);
            }).catch(err => { console.warn("Logistics enrichment/validation failed:", err); setLinksValidated(true); });
          } else {
            setLinksValidated(true);
          }
      }).catch(err => {
          console.warn('[SlowSkeleton] Failed — itinerary continues without logistics:', err);
          setLinksValidated(true);
          // Set empty weather so Expedition Essentials shows "no data" instead of infinite spinner
          setTripData(prev => prev ? {
            ...prev,
            weather: prev.weather || { temperatureRange: 'N/A', condition: 'Unknown', description: 'Weather data unavailable', location: '' } as WeatherData,
            packingList: prev.packingList || [],
            logistics: prev.logistics,
          } : prev);
      });

      const placeholderItinerary: DayItinerary[] = ((skeleton as any).daySummaries?.map((s: any) => ({
          day: s.day,
          moodTitle: s.moodTitle,
          conciergeNotes: s.summary,
          attire: "Consulting archives...",
          transit: "Consulting archives...",
          morning: { activity: "Loading...", description: "Synthesizing your morning experience...", location: "..." },
          afternoon: { activity: "Loading...", description: "Synthesizing your afternoon experience...", location: "..." },
          evening: { activity: "Loading...", description: "Synthesizing your evening experience...", location: "..." },
          isPlaceholder: true
      })) || []);

      const partialTrip = { ...skeleton, itinerary: placeholderItinerary } as TripData;
      setTripData(partialTrip);
      setStep('voyage');
      voyageDisplayed = true;
      setLoadingMessage("");

      // LOCAL HAPPENINGS: search-grounded real events (concerts, exhibitions,
      // markets, festivals) during the travel dates. Background — the section
      // appears when ready and persists via the live vault sync.
      fetchLocalEvents(data.destination, data.startDate, data.endDate)
        .then(events => {
          if (events.length > 0) {
            setTripData(prev => prev ? { ...prev, localEvents: events } : prev);
          }
        })
        .catch(() => { /* section simply doesn't render */ });

      // Create a Firestore placeholder IMMEDIATELY to get a unique trip ID.
      // This gives the itinerary a shareable URL right away (/voyage/{tripId}).
      // Includes skeleton data so the shared link renders something useful.
      // The full data saves at the 90-second mark via auto-save.
      let earlyTripId: string | null = null;
      if (!bypassRateLimit && user && db && auth?.currentUser) {
          try {
              const earlyData = sanitizeForFirestore({
                  ...partialTrip,
                  _placeholder: true,
                  isPublic: true,
                  userId: auth.currentUser.uid,
                  ownerName: auth.currentUser.displayName || user?.name || '',
                  ownerEmail: auth.currentUser.email || user?.email || '',
                  createdAt: serverTimestamp(),
                  userInput: sanitizeForFirestore(data),
                  destination: data.destination,
                  collaborators: [],
                  collaboratorEmails: [],
              });
              const docRef = await addDoc(collection(db, "itineraries"), earlyData);
              earlyTripId = docRef.id;
              setSavedTripId(earlyTripId);
              navigate(`/voyage/${earlyTripId}`, { replace: true });
          } catch (err) {
              console.warn('[Vault] Early ID creation failed — will save later:', err);
          }
      }

      // Logistics enrichment + validation now runs inside slowPromise.then() above
      setLinksValidated(false);

      // AUTO-SAVE TO VAULT: 90 seconds after itinerary is displayed.
      // If earlyTripId exists, UPDATE that placeholder doc with full data.
      // Otherwise create a new doc via saveToVault.
      if (!bypassRateLimit && user) {
          const capturedEarlyId = earlyTripId;
          setTimeout(async () => {
              try {
                  if (!db || !auth?.currentUser) return;
                  const latestTrip = tripDataRef.current;
                  const latestUserData = userDataRef.current;
                  const latestHeader = headerImageRef.current;
                  if (!latestTrip || !latestUserData) return;

                  const tripToSave = { ...latestTrip };
                  if (latestHeader) tripToSave.headerImageUrl = latestHeader;

                  let savedId: string | null = null;

                  if (capturedEarlyId) {
                      // UPDATE the placeholder doc with full trip data + uploaded images
                      try {
                          const rawData = {
                              ...tripToSave,
                              headerImageUrl: latestHeader || tripToSave.headerImageUrl,
                              userInput: latestUserData,
                              userId: auth.currentUser!.uid,
                              ownerName: auth.currentUser!.displayName || user?.name || '',
                              ownerEmail: auth.currentUser!.email || user?.email || '',
                              isPublic: true,
                              _placeholder: false,
                          };
                          // Upload base64 images to Storage before saving
                          const cleanData = await uploadTripImages(capturedEarlyId, rawData, auth.currentUser!.uid);
                          const docRef = doc(db, "itineraries", capturedEarlyId);
                          await setDoc(docRef, sanitizeForFirestore(cleanData), { merge: true });
                          // Update local state with Storage URLs — PRESERVE existing images
                          if (cleanData.itinerary) {
                              // Only update image URLs — don't overwrite weather/packingList/logistics
                              setTripData(prev => {
                                  if (!prev) return null;
                                  const merged = (cleanData.itinerary || []).map((cd: any) => {
                                      const existing = (prev.itinerary || []).find((d: any) => d.day === cd.day);
                                      return { ...existing, ...cd, imageUrl: cd.imageUrl || existing?.imageUrl };
                                  });
                                  return {
                                      ...prev,
                                      itinerary: merged,
                                      headerImageUrl: cleanData.headerImageUrl || prev.headerImageUrl,
                                  };
                              });
                          }
                          if (cleanData.headerImageUrl) setHeaderImage(cleanData.headerImageUrl);
                          savedId = capturedEarlyId;
                          toast.success('Itinerary saved to your vault.');
                      } catch (updateErr) {
                          console.warn('[Vault] Failed to update placeholder, trying full save:', updateErr);
                          savedId = await saveToVault(tripToSave, latestUserData);
                      }
                  } else {
                      savedId = await saveToVault(tripToSave, latestUserData);
                  }

                  if (savedId) {
                      setSavedTripId(savedId);
                      let updatedUserData = { ...latestUserData };
                      let needsUpdate = false;
                      if (latestUserData.customLogo && latestUserData.customLogo.startsWith('data:image')) {
                          const logoUrl = await uploadImageToStorage(latestUserData.customLogo, `users/${auth.currentUser!.uid}/itineraries/${savedId}/logo.png`);
                          if (logoUrl) { updatedUserData.customLogo = logoUrl; needsUpdate = true; }
                      }
                      if (latestUserData.travelerPhoto && latestUserData.travelerPhoto.startsWith('data:image')) {
                          const photoUrl = await uploadImageToStorage(latestUserData.travelerPhoto, `users/${auth.currentUser!.uid}/itineraries/${savedId}/traveler.jpg`);
                          if (photoUrl) { updatedUserData.travelerPhoto = photoUrl; needsUpdate = true; }
                      }
                      if (needsUpdate) {
                          setUserData(updatedUserData);
                          const docRef = doc(db, "itineraries", savedId);
                          await updateDoc(docRef, sanitizeForFirestore({ userInput: updatedUserData }));
                      }
                      navigate(`/voyage/${savedId}`, { replace: true });
                      toast.success('Itinerary saved to your vault.');
                  }
              } catch (err) {
                  console.warn('[Vault] Auto-save failed:', err);
                  toast.warning('Could not save automatically. Use the save button to try again.');
              }
          }, 120000); // 2 minutes — eager save ensures data is available quickly
      }

      // Rotate which time slot anchors each day's image so consecutive days
      // don't all render the same kind of venue (always-afternoon made every
      // image lean toward the same subjects)
      const pickImageAnchor = (d: any): string => {
          const order = ([
              ['morning', 'afternoon', 'evening'],
              ['afternoon', 'evening', 'morning'],
              ['evening', 'morning', 'afternoon'],
          ] as const)[((d.day || 1) - 1) % 3];
          for (const k of order) {
              const loc = d[k]?.location;
              if (loc && typeof loc === 'string' && loc.trim() && loc !== '...') return loc;
          }
          return d.afternoon?.activity || d.moodTitle || '';
      };

      // Mutable reference to preserve image URLs added while days are generating
      const mutableSkeleton = { ...skeleton };
      const aesthetic = data.visualAesthetic;

      // Background day image generation — fires after days start synthesizing
      // Track which days already have image generation in-flight to prevent double-generation
      const imageGenInFlight = new Set<number>();

      // Uses imageQueue (3 concurrent, 300ms delay) so it doesn't block text generation
      const startDayImages = (completedDays: DayItinerary[], targetId?: string | null) => {
          completedDays.forEach((day, index) => {
              if (day.imageUrl) return; // Already has an image (fast-loaded or preserved)
              if (imageGenInFlight.has(day.day)) return; // Already generating from per-day early path
              // Use the venue/location name (not activity) so images are place-specific
              const keyLocation = pickImageAnchor(day);
              if (!keyLocation) return;
              const dayContext = [
                day.morning?.activity && `Morning: ${day.morning.activity}${day.morning.location ? ` at ${day.morning.location}` : ''}`,
                day.afternoon?.activity && `Afternoon: ${day.afternoon.activity}${day.afternoon.location ? ` at ${day.afternoon.location}` : ''}`,
                day.evening?.activity && `Evening: ${day.evening.activity}${day.evening.location ? ` at ${day.evening.location}` : ''}`,
              ].filter(Boolean).join('. ');
              const description = dayContext || day.moodTitle;
              generateLocationImage(keyLocation, description, aesthetic, data.destination, day.day).then(async (img) => {
                  if (!img) return;
                  // Upload to storage eagerly to replace base64 with a URL ASAP
                  const tripId = targetId || savedTripId;
                  const userId = auth?.currentUser?.uid;
                  let finalImg = img;
                  if (tripId && userId && img.startsWith('data:')) {
                      try {
                          const storageUrl = await syncImageToFirestore(
                              `users/${userId}/itineraries/${tripId}/day${day.day}.png`,
                              img, `itinerary.${index}.imageUrl`, tripId
                          );
                          if (storageUrl && !storageUrl.startsWith('data:')) finalImg = storageUrl;
                      } catch (err) {
                          console.warn(`Day ${day.day} image upload failed, using base64:`, err);
                      }
                  }
                  // Update UI state with storage URL (or base64 fallback)
                  setTripData(prev => {
                      if (!prev) return prev;
                      const newItinerary = [...(prev.itinerary || [])];
                      const idx = newItinerary.findIndex(d => d.day === day.day);
                      if (idx !== -1 && !newItinerary[idx].imageUrl) {
                          newItinerary[idx] = { ...newItinerary[idx], imageUrl: finalImg };
                      }
                      return { ...prev, itinerary: newItinerary };
                  });
              }).catch(err => console.warn(`Day ${day.day} background image failed:`, err));
          });
      };

      // Logistics SVG placeholders already set above (before setTripData)

      // 2. Generate Days (Incremental Synthesis)
      setLoadingStep(3);
      setLoadingMessage("Synthesizing " + duration + " days of bespoke experiences...");
      
      const dayTasks = Array.from({ length: duration }, (_, i) => {
          const dayNum = i + 1;
          return () => generateSingleDay(data, skeleton, dayNum).then(async (detailedDay) => {
              // Update state incrementally
              setTripData(prev => {
                  if (!prev) return prev;
                  const newItinerary = [...(prev.itinerary || [])];
                  const index = newItinerary.findIndex(d => d.day === dayNum);
                  if (index !== -1) {
                      // Preserve the image URL if it was already fast-loaded
                      const existingImageUrl = newItinerary[index].imageUrl;
                      newItinerary[index] = { ...detailedDay, imageUrl: existingImageUrl || detailedDay.imageUrl };
                  } else {
                      newItinerary.push(detailedDay);
                  }
                  return { ...prev, itinerary: newItinerary.sort((a, b) => a.day - b.day) };
              });

              // Start image generation immediately for this day (don't wait for all days)
              if (!detailedDay.imageUrl && !imageGenInFlight.has(detailedDay.day)) {
                  imageGenInFlight.add(detailedDay.day);
                  const keyLocation = pickImageAnchor(detailedDay);
                  const dayCtx = [
                    detailedDay.morning?.activity && `Morning: ${detailedDay.morning.activity}${detailedDay.morning.location ? ` at ${detailedDay.morning.location}` : ''}`,
                    detailedDay.afternoon?.activity && `Afternoon: ${detailedDay.afternoon.activity}${detailedDay.afternoon.location ? ` at ${detailedDay.afternoon.location}` : ''}`,
                    detailedDay.evening?.activity && `Evening: ${detailedDay.evening.activity}${detailedDay.evening.location ? ` at ${detailedDay.evening.location}` : ''}`,
                  ].filter(Boolean).join('. ');
                  if (keyLocation) {
                      generateLocationImage(keyLocation, dayCtx || detailedDay.moodTitle, aesthetic, data.destination, detailedDay.day).then(async (img) => {
                          if (!img) return;
                          // Upload to Storage immediately to prevent base64 loss on reload
                          let finalImg = img;
                          const tid = earlyTripId || savedTripId;
                          const uid = auth?.currentUser?.uid;
                          if (tid && uid && img.startsWith('data:')) {
                              try {
                                  const url = await uploadImageToStorage(img, `users/${uid}/itineraries/${tid}/day${detailedDay.day}.png`);
                                  if (url) finalImg = url;
                              } catch { /* fallback to base64 in state */ }
                          }
                          setTripData(prev => {
                              if (!prev) return prev;
                              const newIt = [...(prev.itinerary || [])];
                              const idx = newIt.findIndex(d => d.day === detailedDay.day);
                              if (idx !== -1 && !newIt[idx].imageUrl) {
                                  newIt[idx] = { ...newIt[idx], imageUrl: finalImg };
                              }
                              return { ...prev, itinerary: newIt };
                          });
                          // Persist image URL to Firestore so shared links show images
                          if (tid && db && finalImg && !finalImg.startsWith('data:')) {
                              // Firestore arrays don't support index updates — write full itinerary
                              const snap = tripDataRef.current;
                              if (snap?.itinerary) {
                                const patchedItinerary = snap.itinerary.map(d =>
                                  d.day === detailedDay.day && !d.imageUrl ? { ...d, imageUrl: finalImg } : d
                                );
                                setDoc(doc(db, "itineraries", tid), sanitizeForFirestore({ itinerary: patchedItinerary }), { merge: true })
                                  .catch(e => console.warn(`[Vault] Failed to persist day ${detailedDay.day} image:`, e));
                              }
                          }
                      }).catch(err => console.warn(`Day ${dayNum} early image failed:`, err));
                  }
              }

              return detailedDay;
          }).catch((err) => {
              console.warn(`Day ${dayNum} generation failed:`, err);
              // Replace the stranded "Loading..." placeholder with a failed marker.
              // DailyItinerary already offers per-day refresh in edit mode for retry.
              const failedSlot = { activity: 'Generation interrupted', description: 'Please use Refresh Day to retry.', location: '' };
              const failedDay: DayItinerary = {
                  day: dayNum,
                  moodTitle: `Day ${dayNum} — generation interrupted`,
                  conciergeNotes: 'This day could not be generated. Tap refresh on the day card to retry, or regenerate the trip.',
                  attire: '',
                  transit: '',
                  morning: { ...failedSlot },
                  afternoon: { ...failedSlot },
                  evening: { ...failedSlot },
                  isPlaceholder: false,
              };
              setTripData(prev => {
                  if (!prev) return prev;
                  const newItinerary = [...(prev.itinerary || [])];
                  const index = newItinerary.findIndex(d => d.day === dayNum);
                  if (index !== -1) {
                      newItinerary[index] = { ...newItinerary[index], ...failedDay };
                  } else {
                      newItinerary.push(failedDay);
                  }
                  return { ...prev, itinerary: newItinerary.sort((a, b) => a.day - b.day) };
              });
              return failedDay;
          });
      });

      // Race generation against a 5-minute timeout to prevent infinite loading screen
      const GENERATION_TIMEOUT_MS = 5 * 60 * 1000;
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Trip generation timed out after 5 minutes. Please try again.')), GENERATION_TIMEOUT_MS)
      );
      const days = await Promise.race([throttledPromiseAll(dayTasks, 10, 30), timeoutPromise]);
      logTripSummary();
      setLoadingStep(5);
      setLoadingMessage("Finalizing your itinerary...");

      if (!days || !Array.isArray(days)) {
          throw new Error("Itinerary generation returned invalid data");
      }

      // CRITICAL: Write day content to Firestore IMMEDIATELY — don't wait for eager save.
      // This ensures data survives even if user navigates away before eager save fires.
      const dayWriteId = earlyTripId || savedTripId;
      if (dayWriteId && db) {
          try {
              const currentTrip = tripDataRef.current;
              const headerUrl = headerImageRef.current;
              // Merge in imageUrls already present in state (uploaded by the
              // per-day image flow) — writing bare `days` here used to wipe
              // day images that were already persisted, so shared links could
              // show gradient placeholders the builder never saw.
              const stateDays = currentTrip?.itinerary || [];
              const daysWithImages = days.map((d: any) => {
                  const stateDay = stateDays.find((sd: any) => sd.day === d.day);
                  const img = d.imageUrl || stateDay?.imageUrl;
                  // Only persist https URLs — base64 blows Firestore's 1MB doc limit
                  return img && !String(img).startsWith('data:') ? { ...d, imageUrl: img } : d;
              });
              const dayData = sanitizeForFirestore({
                  itinerary: daysWithImages,
                  _placeholder: false,
                  pipelineStatus: 'days_complete',
                  ...(headerUrl ? { headerImageUrl: headerUrl } : {}),
                  ...(currentTrip?.welcomeNote ? { welcomeNote: currentTrip.welcomeNote } : {}),
                  ...(currentTrip?.destinationSummary ? { destinationSummary: currentTrip.destinationSummary } : {}),
                  ...(currentTrip?.budget ? { budget: currentTrip.budget } : {}),
                  ...(currentTrip?.termsAndConditions ? { termsAndConditions: currentTrip.termsAndConditions } : {}),
                  ...(currentTrip?.logistics ? { logistics: currentTrip.logistics } : {}),
                  ...(currentTrip?.weather ? { weather: currentTrip.weather } : {}),
                  ...(currentTrip?.packingList ? { packingList: currentTrip.packingList } : {}),
                  ...(currentTrip?.eSimRecommendation ? { eSimRecommendation: currentTrip.eSimRecommendation } : {}),
                  ...(currentTrip?.localEvents?.length ? { localEvents: currentTrip.localEvents } : {}),
                  ...(currentTrip?.isInternational !== undefined ? { isInternational: currentTrip.isInternational } : {}),
              });
              await setDoc(doc(db, "itineraries", dayWriteId), dayData, { merge: true });
              console.log('[Vault] Day content persisted to Firestore immediately');
              trackTripGenerated(data.destination, days.length);
          } catch (err) {
              console.warn('[Vault] Immediate day write failed:', err);
          }
      }

      // VENUE STATUS + URL ENRICHMENT (background, sequential):
      // 1. verifyAndReplaceClosedVenues — search-grounded check that swaps any
      //    permanently-closed venue for a verified-open alternative (model
      //    knowledge goes stale; closed restaurants must never be recommended)
      // 2. enrichVenueUrls — look up real website URLs for venues missing them
      // Both update state when done; the live vault sync persists the results.
      verifyAndReplaceClosedVenues(days, data.destination).then(verifiedDays => {
          setTripData(prev => {
              if (!prev) return prev;
              const merged = verifiedDays.map(vd => {
                  const existing = (prev.itinerary || []).find(d => d.day === vd.day);
                  return { ...vd, imageUrl: existing?.imageUrl || vd.imageUrl };
              });
              return { ...prev, itinerary: merged };
          });
          return enrichVenueUrls(verifiedDays, data.destination);
      }).then(enrichedDays => {
          // CRITICAL ORDERING: deep HTTP validation must run AFTER enrichment.
          // Enriched venue URLs (model-recalled, e.g. a real museum domain with
          // an invented /visit/admission-hours path) previously reached users
          // unchecked because validation ran on the pre-enrichment days.
          return deepValidateItineraryLinks(enrichedDays, data.destination);
      }).then(validatedDays => {
          setTripData(prev => {
              if (!prev) return prev;
              const merged = validatedDays.map(ed => {
                  const existing = (prev.itinerary || []).find(d => d.day === ed.day);
                  return { ...ed, imageUrl: existing?.imageUrl || ed.imageUrl };
              });
              return { ...prev, itinerary: merged };
          });
      }).catch(err => console.warn('[VenueURL] Status check/enrichment/validation failed:', err));

      // EAGER SAVE: Save trip data as soon as generation completes — don't wait for the 15s timer
          if (earlyTripId && db && auth?.currentUser) {
            try {
              const eagerTrip = tripDataRef.current;
              const eagerUser = userDataRef.current;
              if (eagerTrip && eagerUser && (eagerTrip.itinerary?.length > 0)) {
                const rawEagerData = {
                  ...eagerTrip,
                  headerImageUrl: headerImageRef.current || eagerTrip.headerImageUrl,
                  userInput: eagerUser,
                  userId: auth.currentUser.uid,
                  ownerName: auth.currentUser.displayName || user?.name || '',
                  ownerEmail: auth.currentUser.email || user?.email || '',
                  isPublic: true,
                  _placeholder: false,
                  pipelineStatus: 'days_complete',
                };                // Upload base64 images to Storage before saving to Firestore
                const cleanData = await uploadTripImages(earlyTripId, rawEagerData, auth.currentUser.uid);
                const eagerDocRef = doc(db, "itineraries", earlyTripId);
                await setDoc(eagerDocRef, sanitizeForFirestore(cleanData), { merge: true });
                // Update local state with Storage URLs — but PRESERVE existing images
                if (cleanData.itinerary) {
                    setTripData(prev => {
                        if (!prev) return null;
                        const mergedItinerary = (cleanData.itinerary || []).map((cleanDay: any) => {
                            const existingDay = (prev.itinerary || []).find((d: any) => d.day === cleanDay.day);
                            return { ...cleanDay, imageUrl: cleanDay.imageUrl || existingDay?.imageUrl };
                        });
                        // Only update image URLs — preserve weather/packingList/logistics from prev
                        return {
                            ...prev,
                            itinerary: mergedItinerary,
                            headerImageUrl: cleanData.headerImageUrl || prev.headerImageUrl,
                        };
                    });
                }
                if (cleanData.headerImageUrl && cleanData.headerImageUrl !== headerImage) {
                    setHeaderImage(cleanData.headerImageUrl);
                }
                console.log('[Vault] Eager save succeeded — trip data is now shareable');
              }
            } catch (err) {
              console.warn('[Vault] Eager save failed, will retry at auto-save:', err);
            }
          }

      // (Deep link validation now runs as the FINAL step of the
      //  verify → enrich → validate chain above, so enriched URLs are checked.)

      // We need fullTrip for the image tasks below, using mutableSkeleton to preserve logistics images
      const fullTrip = { ...mutableSkeleton, itinerary: days } as TripData;

      if (atriumRejections.length > 0) {
          fullTrip.digitalAppendix = atriumRejections;
      }

      // Merge images from current state to preserve fast-loaded images.
      // CRITICAL: Use prev (React state) as the base, NOT fullTrip.
      // fullTrip is built from mutableSkeleton which may predate slow-skeleton
      // fields (weather, packingList, logistics, destinationInsights).
      // By spreading prev first, ALL existing state is preserved.
      // Only the itinerary (synthesized days) and digitalAppendix come from fullTrip.
      setTripData(prev => {
          if (!prev) return fullTrip;
          const mergedItinerary = (fullTrip.itinerary || []).map(d => {
              const existingDay = (prev.itinerary || []).find(ed => ed.day === d.day);
              return { ...d, imageUrl: existingDay?.imageUrl || d.imageUrl };
          });
          return {
              ...prev,
              itinerary: mergedItinerary,
              digitalAppendix: fullTrip.digitalAppendix || prev.digitalAppendix,
          };
      });

      // Calculate Time Saved
      const savedHours = 3.5 + (1.5 * duration);
      setTimeSaved(savedHours);

      // Generate all day images BEFORE saving to vault
      // This ensures the vault gets the complete trip with all images
      const imagePromises: Promise<void>[] = [];
      days.forEach((day, index) => {
          if (day.imageUrl) return;
          if (imageGenInFlight.has(day.day)) return;
          const keyLocation = pickImageAnchor(day);
          if (!keyLocation) return;
          imageGenInFlight.add(day.day);
          const dayContext = [
            day.morning?.activity && `Morning: ${day.morning.activity}${day.morning.location ? ` at ${day.morning.location}` : ''}`,
            day.afternoon?.activity && `Afternoon: ${day.afternoon.activity}${day.afternoon.location ? ` at ${day.afternoon.location}` : ''}`,
            day.evening?.activity && `Evening: ${day.evening.activity}${day.evening.location ? ` at ${day.evening.location}` : ''}`,
          ].filter(Boolean).join('. ');
          const description = dayContext || day.moodTitle;
          const p = generateLocationImage(keyLocation, description, aesthetic, data.destination, day.day).then(async (img) => {
              if (!img) return;
              // Upload to Storage immediately
              let finalImg = img;
              const tid = earlyTripId || savedTripId;
              const uid = auth?.currentUser?.uid;
              if (tid && uid && img.startsWith('data:')) {
                  try {
                      const url = await uploadImageToStorage(img, `users/${uid}/itineraries/${tid}/day${day.day}.png`);
                      if (url) finalImg = url;
                  } catch { /* fallback to base64 */ }
              }
              // Update the fullTrip object so vault save gets the Storage URL
              const idx = fullTrip.itinerary?.findIndex(d => d.day === day.day) ?? -1;
              if (idx !== -1 && fullTrip.itinerary) fullTrip.itinerary[idx].imageUrl = finalImg;
              // Also update UI state immediately
              setTripData(prev => {
                  if (!prev) return prev;
                  const newItinerary = [...(prev.itinerary || [])];
                  const i = newItinerary.findIndex(d => d.day === day.day);
                  if (i !== -1 && !newItinerary[i].imageUrl) {
                      newItinerary[i] = { ...newItinerary[i], imageUrl: finalImg };
                  }
                  return { ...prev, itinerary: newItinerary };
              });
          }).catch(err => console.warn(`Day ${day.day} image failed:`, err));
          imagePromises.push(p);
      });

      // Also wait for header image
      const headerImg = await headerImagePromise.catch(() => null);
      if (headerImg) {
          fullTrip.headerImageUrl = headerImg;
          setHeaderImage(headerImg);
          setTripData(prev => prev ? { ...prev, headerImageUrl: headerImg } : null);
      }

      // Wait for all day images to complete (60s timeout)
      await Promise.race([
          Promise.allSettled(imagePromises),
          new Promise(resolve => setTimeout(resolve, 60000))
      ]);

      // Sync fullTrip with current state (images may have arrived via setTripData)
      setTripData(prev => {
          if (prev) {
              (prev.itinerary || []).forEach((day, idx) => {
                  if (day.imageUrl && idx < (fullTrip.itinerary || []).length) {
                      (fullTrip.itinerary || [])[idx] && (fullTrip.itinerary[idx].imageUrl = day.imageUrl);
                  }
              });
              if (prev.headerImageUrl) fullTrip.headerImageUrl = prev.headerImageUrl;
          }
          return prev;
      });

      // Update pipeline status — images are done
      const imgTripId = earlyTripId || savedTripId;
      if (imgTripId && db) {
          updateDoc(doc(db, "itineraries", imgTripId), { pipelineStatus: 'images_complete' }).catch(() => {});
      }

      // 3. CREDIT GUARD: Deduct 1 ticket only AFTER everything has loaded
      // (days synthesized, images generated, page fully rendered)
      if (!bypassRateLimit && user && db)  {
          const creditToken = await ensureAuthenticated();
          if (creditToken) {
              const currentUser = userRef.current!;
              const userDocRef = doc(db, "users", currentUser.id);
              const newCredits = currentUser.credits - 1;
              await updateDoc(userDocRef, {
                  credits: increment(-1),
                  lastSearchTimestamp: Date.now()
              });
              setUser({ ...currentUser, credits: newCredits, lastSearchTimestamp: Date.now() });
              toast.info(`1 Ticket Used. Remaining: ${newCredits}`);
          } else {
              console.warn('[Auth] Token expired during generation - credit deduction deferred');
          }
      }

      // Auto-save is scheduled above (90s after setStep('voyage'))

    } catch (error: any) {
      console.error("Voyage generation failed", error);
      const errorMsg = error?.message || "Unknown error";
      const isAuthError = error?.code === 'permission-denied' || error?.code === 'unauthenticated' || error?.message?.includes('Missing or insufficient permissions');

      // If the trip is already displayed (step === 'voyage'), don't kick the user back.
      // Save/upload failures after the trip is visible should only show a toast.
      const tripAlreadyVisible = step === 'voyage' && tripData;
      // Only a true generation failure counts — post-display save/upload errors
      // don't. Uses the local flag: the `step` in this closure is stale.
      if (!voyageDisplayed) trackGenerateFail('voyage', errorMsg.slice(0, 100), data.destination);

      if (tripAlreadyVisible) {
          console.warn('[Post-generation error — trip stays visible]', errorMsg);
          if (errorMsg.includes('Save timed out') || errorMsg.includes('upload')) {
              toast.warning('Your itinerary is displayed but saving was slow. It will sync when your connection improves.');
          } else {
              toast.warning('Your itinerary is displayed but could not be saved to your vault. Please try saving manually.');
          }
      } else if (isAuthError && tripData && userData) {
          // Auth failed during generation - backup whatever we have
          backupTripToLocalStorage({
              tripData,
              userData,
              headerImage,
              savedAt: Date.now(),
              userId: user?.id || 'unknown'
          });
          setAuthError("Your session expired during generation. Your itinerary has been saved locally. Please sign in again to restore it.");
      } else {
          const isRateLimit = errorMsg.includes('429') || errorMsg.toLowerCase().includes('too many') || errorMsg.toLowerCase().includes('rate');
          const isAuthIssue = errorMsg.includes('401') || errorMsg.toLowerCase().includes('unauthorized');
          if (isRateLimit) {
              toast.error('The archives are momentarily saturated. Please wait a moment and try again.');
          } else if (isAuthIssue) {
              toast.error('Your session may have expired. Please sign in again.');
          } else {
              toast.error('Something went wrong generating your itinerary. Please try again.');
              console.error('[Generation Error Detail]', errorMsg);
          }
      }
      if (!tripAlreadyVisible) {
          setStep('onboarding');
          hasInitialized.current = false;
      }
    } finally {
      isGeneratingRef.current = false;
    }
  };

  const handleOnboardingSubmit = async (data: UserInput) => {
    try {
        setUserData(data);
        if (!data.destination || data.destination.trim() === '') {
            setStep('selecting_dest');
        } else {
            startAtriumPhase(data);
        }
    } catch (err) {
        console.error("App: Error in handleOnboardingSubmit:", err);
    }
  };

  const handleDestinationSelected = (destination: string) => {
    if (userData) {
        const updatedData = { ...userData, destination };
        setUserData(updatedData);
        startAtriumPhase(updatedData);
    }
  };

  const handleDayUpdate = (newDay: DayItinerary) => {
    if (!tripData) return;
    const updatedItinerary = (tripData.itinerary || []).map(day =>
      day.day === newDay.day ? newDay : day
    );
    setTripData({ ...tripData, itinerary: updatedItinerary });
  };

  const handleDayDragEnd = (result: any) => {
    if (!result.destination || !tripData) return;
    if (result.source.index === result.destination.index) return;
    const reordered = Array.from(tripData.itinerary);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    // Re-number days and re-assign dates
    const renumbered = reordered.map((day: any, i: number) => ({
      ...day,
      day: i + 1,
    }));
    setTripData({ ...tripData, itinerary: renumbered });
  };

  // Shared helper: upload base64 image to Storage with timeout, return hosted URL or original
  const uploadImageWithTimeout = async (base64: string, storagePath: string): Promise<string> => {
      if (!base64.startsWith('data:image') || base64.startsWith('data:image/svg')) return base64;
      const userId = auth?.currentUser?.uid;
      const fullPath = userId ? `users/${userId}/${storagePath}` : storagePath;
      const uploadPromise = uploadImageToStorage(base64, fullPath);
      const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
      const uploadedUrl = await Promise.race([uploadPromise, timeoutPromise]);
      return uploadedUrl || base64;
  };

  // Shared helper: update an array-nested field in Firestore with a single read
  const updateArrayFieldInFirestore = async (
      docRef: ReturnType<typeof doc>,
      fieldPath: string,
      finalUrl: string
  ) => {
      // For array fields (airbnbs, restaurants), read doc once then update the whole array
      const arrayMatch = fieldPath.match(/^logistics\.(airbnbs|restaurants)\.(\d+)/);
      if (arrayMatch) {
          const [, arrayName, indexStr] = arrayMatch;
          const index = parseInt(indexStr);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
              const items = docSnap.data().logistics?.[arrayName] || [];
              if (items[index]) {
                  items[index] = { ...items[index], imageUrl: finalUrl };
                  await updateDoc(docRef, { [`logistics.${arrayName}`]: items });
              }
          }
      } else {
          await updateDoc(docRef, { [fieldPath]: finalUrl });
      }
  };

  const syncImageToFirestore = async (path: string, base64: string, fieldPath: string, tripId?: string) => {
      const targetId = tripId || savedTripId;
      if (!db || !targetId) return base64;

      try {
          const finalUrl = await uploadImageWithTimeout(base64, `itineraries/${targetId}/${path}`);
          const docRef = doc(db, "itineraries", targetId);

          // Fire and forget the Firestore update so it doesn't block the UI
          updateArrayFieldInFirestore(docRef, fieldPath, finalUrl)
              .catch(() => toast.error('Image sync failed. Changes may not be saved.'));

          return finalUrl;
      } catch (e) {
          console.error(`Sync failed for ${fieldPath}`, e);
          return base64;
      }
  };

  const handleDayImageUpdate = async (dayNum: number, imageUrl: string, overrideId?: string) => {
      const targetId = overrideId || savedTripId;

      // Update UI immediately with whatever URL we have
      setTripData(prev => {
          if(!prev) return null;
          return {
              ...prev,
              itinerary: (prev.itinerary || []).map(d => d.day === dayNum ? { ...d, imageUrl: imageUrl } : d)
          };
      });

      // Fire and forget the background sync
      const backgroundSync = async () => {
          const finalUrl = await uploadImageWithTimeout(imageUrl, `itineraries/${targetId}/day${dayNum}.png`);

          if (finalUrl !== imageUrl) {
              // Update UI again with the permanent URL
              setTripData(prev => {
                  if(!prev) return null;
                  return {
                      ...prev,
                      itinerary: (prev.itinerary || []).map(d => d.day === dayNum ? { ...d, imageUrl: finalUrl } : d)
                  };
              });
          }

          // Update Firestore — single read, single write
          if (db && targetId) {
              const docRef = doc(db, "itineraries", targetId);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                  const currentItinerary = docSnap.data().itinerary || [];
                  const updatedItinerary = currentItinerary.map((d: any) =>
                      d.day === dayNum ? { ...d, imageUrl: finalUrl } : d
                  );
                  await updateDoc(docRef, sanitizeForFirestore({ itinerary: updatedItinerary }));
              }
          }
      };

      backgroundSync().catch(() => toast.error('Image sync failed. Changes may not be saved.')); // Do not await
  };

  const handleLogisticsUpdate = (newLogistics: LogisticsData) => {
      if (!tripData) return;
      setTripData({ ...tripData, logistics: newLogistics });
  };

  const handleBookingStatusChange = (type: 'flight' | 'hotel' | 'flightAlt' | 'hotelAlt', index: number, lock: BookingLock) => {
      if (!tripData) return;
      const newLogistics = { ...tripData.logistics };
      if (type === 'flight') {
        newLogistics.flight = { ...newLogistics.flight, bookingLock: lock };
      } else if (type === 'hotel') {
        newLogistics.hotel = { ...newLogistics.hotel, bookingLock: lock };
      } else if (type === 'flightAlt' && newLogistics.flightAlternatives) {
        const alts = [...newLogistics.flightAlternatives];
        alts[index] = { ...alts[index], bookingLock: lock };
        newLogistics.flightAlternatives = alts;
      } else if (type === 'hotelAlt' && newLogistics.hotelAlternatives) {
        const alts = [...newLogistics.hotelAlternatives];
        alts[index] = { ...alts[index], bookingLock: lock };
        newLogistics.hotelAlternatives = alts;
      }
      setTripData({ ...tripData, logistics: newLogistics });
      if (db && savedTripId) {
        const docRef = doc(db, "itineraries", savedTripId);
        updateDoc(docRef, sanitizeForFirestore({ logistics: newLogistics })).catch(e => { console.error("Failed to save booking status", e); toast.error("The Concierge couldn\u2019t save your booking update."); });
      }
  };

  const handleActivityBookingLockChange = (dayNum: number, section: 'morning' | 'afternoon' | 'evening', lock: BookingLock) => {
      if (!tripData) return;
      const updatedItinerary = (tripData.itinerary || []).map(day => {
        if (day.day !== dayNum) return day;
        return { ...day, [section]: { ...day[section], bookingLock: lock } };
      });
      setTripData({ ...tripData, itinerary: updatedItinerary });
      if (db && savedTripId) {
        const docRef = doc(db, "itineraries", savedTripId);
        updateDoc(docRef, sanitizeForFirestore({ itinerary: updatedItinerary })).catch(e => { console.error("Failed to save activity booking lock", e); toast.error("The Concierge couldn\u2019t lock this booking."); });
      }
  };

  const handleWelcomeNoteUpdate = (note: string) => {
      if (!tripData) return;
      setTripData({ ...tripData, welcomeNote: note });
  }

  const handleTermsUpdate = (terms: string) => {
      if (!tripData) return;
      setTripData({ ...tripData, termsAndConditions: terms });
  }

  const saveChanges = async () => {
      if (!db || !savedTripId || !tripData || !userData) return;

      // Ensure auth token is valid before saving
      const token = await ensureAuthenticated();
      if (!token) {
          setAuthError("Your session has expired. Please sign in again to save your changes.");
          return;
      }

      setIsSaving(true);
      try {
          const rawData = sanitizeForFirestore({
              ...tripData,
              headerImageUrl: headerImage,
              userInput: userData,
              updatedAt: Date.now()
          });

          // Upload any remaining base64 images to Storage before saving to Firestore
          const userId = auth?.currentUser?.uid;
          const cleanData = await uploadTripImages(savedTripId, rawData, userId);

          const docRef = doc(db, "itineraries", savedTripId);
          await updateDoc(docRef, sanitizeForFirestore(cleanData));

          // Update local state with cleaned URLs
          if (cleanData.headerImageUrl && cleanData.headerImageUrl !== headerImage) {
              setHeaderImage(cleanData.headerImageUrl);
          }
          if (cleanData.itinerary) {
              setTripData(prev => prev ? { ...prev, ...cleanData, headerImageUrl: cleanData.headerImageUrl } : null);
          }
          
          toast.success('Itinerary saved to vault');

          // Update offline cache with edited dossier (PWA)
          if (savedTripId) {
              cacheDossierOffline(savedTripId, cleanData);
          }

          setIsEditing(false);
      } catch (error) {
          console.error("Error saving changes:", error);
          toast.error('The Concierge couldn\u2019t save your changes. Please try again.');
      } finally {
          setIsSaving(false);
      }
  };

  // --- MAP SYNC HANDLER ---
  const handleLocationFocus = (lat: number, lng: number) => {
      setActiveLocation({ lat, lng });
  };

  const handleCopyEmailDossier = async () => {
    if (!tripData || !userData) return;
    try {
      const html = generateEmailHTML(tripData, userData, userData, brand);
      const success = await copyEmailToClipboard(html);
      if (success) {
        toast.success('Itinerary copied to clipboard');
      } else {
        toast.error('Clipboard access denied. Please try again.');
      }
    } catch (error) {
      console.error("Email dossier generation failed:", error);
      toast.error('The Concierge couldn\u2019t compose the email. Please try again.');
    }
  };

  const shareLink = async () => {
      trackTripShared();
      if (!db || !tripData || !userData) {
          toast.warning('Itinerary data is incomplete.');
          return;
      }

      // Auto-save to vault if not yet saved — ensures we have a unique URL to share
      let activeTripId = savedTripId;
      if (!activeTripId) {
          toast.info('Saving to vault first...');
          const newId = await saveToVault(tripData, userData);
          if (!newId) {
              toast.error('Could not save itinerary. Please try again.');
              return;
          }
          activeTripId = newId;
      }

      if (!activeTripId) {
          toast.error('Could not generate share link. Please save to vault first.');
          return;
      }

      // Ensure auth token is valid before the Firestore write
      const token = await ensureAuthenticated();
      if (!token) {
          setAuthError("Your session has expired. Please sign in again to share your itinerary.");
          return;
      }

      setIsSharing(true);
      try {
          // Read current visibility so the share dialog reflects reality.
          // The dialog lets the owner choose: public (anyone with the link)
          // or private (owner + invited collaborators only).
          const docRef = doc(db, "itineraries", activeTripId);
          const docSnap = await getDoc(docRef);
          setShareIsPublic(docSnap.exists() ? docSnap.data().isPublic !== false : true);
          setIsShareModalOpen(true);
      } catch (err) {
          console.error("Share failed", err);
          toast.error('The share link couldn\u2019t be minted. Please try again.');
      } finally {
          setIsSharing(false);
      }
  };

  const handleShareVisibilityChange = async (makePublic: boolean) => {
      if (!db || !savedTripId) return;
      await updateDoc(doc(db, "itineraries", savedTripId), { isPublic: makePublic });
      setShareIsPublic(makePublic);
  };

  const handleEmailDispatch = async (recipientEmail: string): Promise<boolean> => {
      if (!userData || (!savedTripId && !tripData?.seed)) {
          toast.warning('Unable to email this dossier. Try regenerating it first.');
          return false;
      }
      if (!(await consumeCredit())) return false;

      try {
        // EXACT-COPY GUARANTEE: always email the saved trip's URL so the
        // recipient opens the very same stored itinerary the sender sees.
        // The legacy {input, seed} regeneration link produced a DIFFERENT
        // itinerary on the recipient's device (LLM generation is not
        // deterministic) — only fall back to it when the trip was never saved.
        let shareUrl: string;
        if (savedTripId && db) {
            try {
                const snap = await getDoc(doc(db, "itineraries", savedTripId));
                if (snap.exists() && snap.data().isPublic === false) {
                    // Trip is private — grant the recipient viewer access
                    // instead of silently making the trip public.
                    const { addCollaborator } = await import('./services/collaborationService');
                    await addCollaborator(savedTripId, recipientEmail, 'viewer', auth?.currentUser?.uid || '').catch(() => {});
                } else if (snap.exists() && !snap.data().isPublic) {
                    await updateDoc(doc(db, "itineraries", savedTripId), { isPublic: true });
                }
            } catch { /* visibility unchanged — owner's link still works */ }
            shareUrl = `${window.location.origin}/voyage/${savedTripId}`;
        } else {
            const { customLogo, travelerPhoto, ...safeInput } = userData;
            const payload = { input: safeInput, seed: tripData!.seed };
            const encoded = safeEncode(payload);
            const url = new URL(window.location.origin + window.location.pathname);
            const voyageName = userData.destination ? userData.destination.split(',')[0].trim() : 'Voyage';
            url.searchParams.set('voyage', voyageName);
            url.searchParams.set('data', encoded);
            shareUrl = url.toString();
        }

        // Build a text summary for the email body
        let summary = `${userData.destination}\n`;
        summary += `${userData.startDate} to ${userData.endDate}\n`;
        summary += `Traveler: ${userData.clientName}${userData.companionName ? ' & ' + userData.companionName : ''}\n`;
        summary += `Vibe: ${userData.vibe?.join(', ') || ''}\n`;

        if (tripData.itinerary) {
          summary += `\n${tripData.itinerary.length}-day itinerary:\n`;
          tripData.itinerary.forEach(day => {
            summary += `  Day ${day.day} — ${day.moodTitle}\n`;
          });
        }

        if (tripData.budget) {
          summary += `\nEstimated Investment: ${tripData.budget?.totalRange || ''}`;
        }

        const token = await auth?.currentUser?.getIdToken();
        if (!token) {
          toast.error('Please sign in to send itineraries.');
          return false;
        }

        const res = await fetch('/api/share-trip', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            recipientEmail,
            destination: userData.destination,
            shareUrl,
            senderName: userData.clientName || userData.agencyName || brand.name,
            itinerarySummary: summary,
          }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          toast.error(data.error || 'Failed to send itinerary.');
          return false;
        }

        toast.success('Itinerary dispatched.');
        return true;
      } catch (err) {
        console.error("Email dispatch failed", err);
        toast.error('The email couldn\u2019t be sent. Please try again.');
        return false;
      }
  };

  const handleDashboardSelect = (trip: SavedTrip) => {
      setTripData({ ...trip, itinerary: trip.itinerary || [] });
      setUserData(trip.userInput || {} as any);
      setSavedTripId(trip.id);
      setIsPublicView(false);
      setStep('voyage');

      // Determine collaboration role
      const currentUid = auth?.currentUser?.uid || '';
      const currentEmail = auth?.currentUser?.email || '';

      if (trip.userId === currentUid) {
        // User is the owner
        setIsCollaborativeView(false);
        setCollaborationRole('owner');
        setTripOwnerName('');
      } else if (currentEmail && isCollaborator(trip, currentEmail)) {
        // User is a collaborator
        setIsCollaborativeView(true);
        const collabInfo = getCollaboratorInfo(trip, currentEmail);
        setCollaborationRole(collabInfo?.role || 'viewer');
        setTripOwnerName(trip.ownerName || trip.userInput?.clientName || 'the owner');

        // Auto-accept collaboration on first view
        if (collabInfo?.status === 'pending') {
          acceptCollaboration(trip.id, currentEmail).catch(console.error);
        }
      } else {
        setIsCollaborativeView(false);
        setCollaborationRole('owner');
        setTripOwnerName('');
      }

      // Store collaborators for display
      setTripCollaborators(trip.collaborators || []);

      // Update URL to reflect unique trip ID
      navigate(`/voyage/${trip.id}`, { replace: true });

      // Trigger image generation if missing
      if (!headerImage && trip.userInput?.destination) {
         generateVogueImage(trip.userInput.destination, "iconic landmark", trip.userInput.visualAesthetic, trip.userInput.destination)
            .then(img => { if(img) setHeaderImage(img); });
      }
  };

  const handleNewTrip = (skipConfirm = false) => {
      if (!skipConfirm && tripData && !savedTripId) {
        if (!window.confirm('You have an unsaved itinerary. Start a new trip anyway?')) return;
      }
      setStep('onboarding');
      setTripData(null);
      setUserData(null);
      setSavedTripId(null);
      setIsPublicView(false);
      setHeaderImage(undefined);
      setIsCollaborativeView(false);
      setCollaborationRole('owner');
      setTripOwnerName('');
      setTripCollaborators([]);
  };

  const handleBrowseTemplates = () => {
      setStep('templates');
  };

  const handleTemplateSelect = (prefilledInput: Partial<UserInput>) => {
      // Merge prefilled template values into a fresh form and go to onboarding
      setUserData(prefilledInput as UserInput);
      setTripData(null);
      setSavedTripId(null);
      setIsPublicView(false);
      setHeaderImage(undefined);
      // Navigate to onboarding with the prefilled data
      setStep('onboarding');
      toast.success('Template applied \u2014 customize your details and generate!');
  };

  // --- RENDER ---

  // Ensure body background matches current phase — prevents white flash between states
  React.useEffect(() => {
    if (authLoading || !user || showLanding) {
      document.body.style.backgroundColor = '#0A0A0A';
    } else if (step === 'voyage') {
      document.body.style.backgroundColor = '#FAF9F6';
    } else {
      document.body.style.backgroundColor = '#FAF9F6';
    }
  }, [authLoading, user, showLanding, step]);

  // Show auth loading spinner ONLY when there's no trip URL to load.
  // Trip URLs bypass this gate — the trip starts loading immediately in parallel with auth.
  if (authLoading && !urlTripId) return (
    <div className="fixed inset-0 bg-charcoal flex items-center justify-center">
      <div style={{ opacity: 0, animation: 'fadeInUp 600ms cubic-bezier(0.16, 1, 0.3, 1) 100ms forwards' }} className="flex flex-col items-center gap-4">
        <AirplaneIcon className="w-10 h-10 text-gold" style={{ animation: 'float 3s ease-in-out infinite' } as React.CSSProperties} />
        <span className="font-display text-lg tracking-[0.4em] text-ivory uppercase">Aura Traveler</span>
      </div>
      <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes fadeOut { to { opacity: 0; pointer-events: none; } }
      `}</style>
    </div>
  );
  if (showLanding) return <LandingPage onEnter={() => setShowLanding(false)} />;
  if (!user && !urlTripId) return <Suspense fallback={<LoadingScreen />}><AuthScreen onLogin={handleLogin} /></Suspense>;
  if (showPayment) return <ErrorBoundary level="page"><Suspense fallback={<LoadingScreen />}><PaymentGateway user={user} onSuccess={handlePaymentSuccess} onLogout={handleLogout} onClose={() => setShowPayment(false)} onUserUpdate={(updates) => setUser(prev => prev ? { ...prev, ...updates } : prev)} mode={paymentMode} /></Suspense></ErrorBoundary>;

  const renderPastDueBanner = () => {
    if (user?.subscriptionStatus !== 'past_due') return null;
    return (
      <div className="fixed top-0 left-0 right-0 z-[45] bg-amber-500/95 backdrop-blur-sm text-white py-2.5 px-4 no-print">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs font-serif">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span>Your last payment failed. Please update your payment method to continue using premium features.</span>
          </div>
          <button
            onClick={() => { setPaymentMode('manage'); setShowPayment(true); }}
            className="flex-shrink-0 px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-[10px] uppercase tracking-widest font-sans rounded-sm transition-colors"
          >
            Update Payment
          </button>
        </div>
      </div>
    );
  };

  const renderGlobalControls = () => {
    if (!user) return null;
    return (
      <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-40 no-print flex flex-col sm:flex-row gap-2 sm:gap-4 items-end sm:items-center">
          <div className="relative">
              <button
                data-tour="user-menu"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="w-12 h-12 rounded-full overflow-hidden border border-sand hover:border-gold transition-colors shadow-lg"
              >
                  {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                      <div className="w-full h-full bg-charcoal text-white flex items-center justify-center font-serif text-lg">{(user.name || '?').charAt(0)}</div>
                  )}
              </button>
              {isUserMenuOpen && (
                  <div className="absolute top-14 right-0 bg-ivory border border-gray-100 shadow-xl w-48 max-w-[calc(100vw-32px)] py-2 animate-fadeIn rounded-sm">
                      <div className="px-4 py-2 border-b border-gray-100 mb-2">
                          <p className="font-sans text-[10px] uppercase tracking-widest text-gold">Signed In</p>
                          <p className="font-serif text-sm truncate text-charcoal">{user.name}</p>
                          <p className="font-mono text-[10px] text-gray-600 mt-1">Tickets: {user.credits}</p>
                      </div>
                      <button
                          data-tour="vault-link"
                          onClick={() => {
                            if (isEditing) {
                              if (!window.confirm('You have unsaved edits. Leave without saving?')) return;
                              setIsEditing(false);
                            }
                            setStep('dashboard'); setIsUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gray-50 transition-colors"
                      >
                          The Vault
                      </button>
                      <button
                          onClick={() => { setStep('templates'); setIsUserMenuOpen(false); }}
                          className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gray-50 transition-colors"
                      >
                          Templates
                      </button>
                      <button
                          onClick={() => { setPaymentMode('upgrade'); setShowPayment(true); setIsUserMenuOpen(false); }}
                          className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-gold hover:bg-gray-50 transition-colors"
                      >
                          Top Up Tickets
                      </button>
                      <button
                          onClick={() => { setPaymentMode('manage'); setShowPayment(true); setIsUserMenuOpen(false); }}
                          className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                          <svg className="w-3 h-3 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                          Manage Billing
                      </button>
                      <button
                          onClick={() => { setIsWhiteLabelOpen(true); setIsUserMenuOpen(false); }}
                          className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                          {brand.isWhiteLabel && <span className="w-2 h-2 rounded-full bg-gold inline-block"></span>}
                          White Label
                      </button>
                      {step === 'voyage' && (
                        <>
                          <div className="border-t border-gray-100 mt-1 mb-1"></div>
                          <button onClick={() => { shareLink(); setIsUserMenuOpen(false); }} disabled={isSharing} className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gray-50 transition-colors">
                            {isSharing ? 'Preparing...' : 'Share Itinerary'}
                          </button>
                          <button onClick={() => { setIsEmailModalOpen(true); setIsUserMenuOpen(false); }} className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gray-50 transition-colors">
                            Email Itinerary
                          </button>
                          <button onClick={() => { handleCopyEmailDossier(); setIsUserMenuOpen(false); }} className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gray-50 transition-colors">
                            Copy to Clipboard
                          </button>
                          <button onClick={() => { setIsAuraStudioOpen(true); setIsUserMenuOpen(false); }} className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-gold hover:bg-gray-50 transition-colors">
                            Aura Studio
                          </button>
                          <button onClick={() => { setIsMementoOpen(true); setIsUserMenuOpen(false); }} className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gray-50 transition-colors">
                            Social Card
                          </button>
                          <button onClick={() => { setIsVeoStudioOpen(true); setIsUserMenuOpen(false); }} className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gray-50 transition-colors">
                            Video Reel
                          </button>
                        </>
                      )}
                      <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-wider text-gray-600 hover:text-charcoal hover:bg-gray-50 transition-colors">Sign Out</button>
                      <button onClick={() => { resetOnboarding(); setShowOnboardingTour(true); setIsUserMenuOpen(false); }} className="w-full text-left px-4 py-2 text-xs font-sans uppercase tracking-widest text-gray-500 hover:text-charcoal hover:bg-gray-50 transition-colors">Take the Tour</button>
                  </div>
              )}
          </div>
          <button onClick={() => { resetOnboarding(); setShowOnboardingTour(true); }} className="w-12 h-12 flex items-center justify-center rounded-full shadow-lg border transition-all bg-ivory/90 text-charcoal border-sand hover:border-gold" title="Take a tour">
              <HelpCircle className="w-5 h-5" />
          </button>
          {step === 'voyage' && (
              <>
                  {/* Edit/Reorder buttons: hidden for viewer collaborators */}
                  {collaborationRole !== 'viewer' && (
                    <>
                      {isEditing && (
                          <button
                            onClick={saveChanges}
                            disabled={isSaving}
                            className="bg-emerald-600 text-white px-6 py-3 font-display text-xs uppercase tracking-widest shadow-lg hover:bg-emerald-500 transition-colors disabled:opacity-50 flex items-center gap-2"
                          >
                              {isSaving ? 'Saving...' : 'Save Changes'}
                          </button>
                      )}
                      <button onClick={() => setIsEditing(!isEditing)} className={`w-12 h-12 flex items-center justify-center rounded-full shadow-lg border transition-all ${isEditing ? 'bg-gold text-charcoal border-charcoal' : 'bg-ivory/90 text-charcoal border-sand hover:border-gold'}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" /></svg>
                      </button>
                      <button
                        onClick={() => setIsReorderMode(!isReorderMode)}
                        className={`w-12 h-12 flex items-center justify-center rounded-full shadow-lg border transition-all ${isReorderMode ? 'bg-gold text-charcoal border-charcoal' : 'bg-ivory/90 text-charcoal border-sand hover:border-gold'}`}
                        title={isReorderMode ? 'Exit Reorder Mode' : 'Reorder Days'}
                      >
                          <ArrowUpDown className="w-5 h-5" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      // Ask Aura is a fixed-position popup now — no scroll needed,
                      // just click its floating launcher to open the panel
                      const toggle = document.querySelector<HTMLButtonElement>('button[data-ask-aura-toggle]');
                      toggle?.click();
                    }}
                    className="bg-charcoal text-gold w-12 h-12 flex items-center justify-center rounded-full shadow-lg border border-gold hover:bg-gold hover:text-charcoal transition-colors animate-pulse"
                    title="Ask your AI Concierge"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" /></svg>
                  </button>
                  <button onClick={() => setIsAuraStudioOpen(true)} className="hidden sm:inline-flex bg-charcoal text-gold px-6 py-3 font-display text-xs uppercase tracking-widest shadow-lg hover:bg-gold hover:text-charcoal transition-colors border border-gold/30 items-center gap-2">
                       <Sparkles className="w-4 h-4" />
                       Social Card
                  </button>
                   {/* Desktop-only: Calendar, Share, Vault buttons */}
                   {tripData && userData && (
                       <div className="relative hidden sm:block">
                         <button onClick={() => setCalendarMenuOpen(!calendarMenuOpen)} className="flex bg-ivory/90 text-charcoal px-6 py-3 font-display text-xs uppercase tracking-widest shadow-lg hover:bg-gold hover:text-white transition-colors border border-sand items-center gap-2 rounded-lg">
                             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" /></svg>
                             Calendar
                         </button>
                         {calendarMenuOpen && (
                           <>
                             <div className="fixed inset-0 z-40" onClick={() => setCalendarMenuOpen(false)} />
                             <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border border-sand/50 overflow-hidden z-50 min-w-[200px]">
                               <button onClick={() => { generateGoogleCalendarExport(tripData, userData); setCalendarMenuOpen(false); }} className="w-full text-left px-4 py-3 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gold/10 transition-colors flex items-center gap-3">
                                 <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none"><path d="M18 3h-1V1h-2v2H9V1H7v2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2z" fill="#4285F4"/><path d="M6 9h12v12H6z" fill="#fff"/><path d="M10.5 17.5l-2.25-2.25 1.06-1.06 1.19 1.19 3.19-3.19 1.06 1.06L10.5 17.5z" fill="#34A853"/></svg>
                                 Google Calendar
                               </button>
                               <button onClick={() => { generateCalendarExport(tripData, userData); setCalendarMenuOpen(false); }} className="w-full text-left px-4 py-3 text-xs font-sans uppercase tracking-widest text-charcoal hover:bg-gold/10 transition-colors flex items-center gap-3 border-t border-sand/30">
                                 <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" fill="#FF3B30"/><path d="M3 9h18v10a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" fill="#fff"/><rect x="6.75" y="1" width="1.5" height="5" rx=".75" fill="#FF3B30"/><rect x="15.75" y="1" width="1.5" height="5" rx=".75" fill="#FF3B30"/></svg>
                                 Apple Calendar
                               </button>
                             </div>
                           </>
                         )}
                       </div>
                   )}
                   {savedTripId && (
                       <button onClick={shareLink} disabled={isSharing} className="hidden sm:flex bg-gold text-charcoal px-6 py-3 font-display text-xs uppercase tracking-widest shadow-lg hover:bg-white transition-colors items-center gap-2 rounded-lg">
                           {isSharing ? 'Sharing...' : 'Share'}
                       </button>
                   )}
                   {tripData && userData && (
                       <button
                         onClick={() => saveToVault(tripData, userData)}
                         disabled={isSaving}
                         className="hidden sm:flex px-6 py-3 font-display text-xs uppercase tracking-widest shadow-lg transition-all items-center gap-2 press-haptic rounded-lg bg-gold text-charcoal hover:bg-white"
                       >
                           {isSaving ? (
                             <><div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> Saving...</>
                           ) : 'Save to Vault'}
                       </button>
                   )}
              </>
          )}
      </div>
  );
  };

  const getLogoColor = () => {
    // Steps that are always light
    if (step === 'dashboard' || step === 'selecting_dest' || step === 'templates') {
      return 'text-charcoal';
    }
    // Steps that support dark mode (Onboarding, Atrium, Voyage)
    return 'text-charcoal';
  };

  const renderLogo = () => {
    // Hide logo on screens that have their own centered branding or where it's redundant
    if (step === 'onboarding' || step === 'selecting_dest' || step === 'atrium_loading' || step === 'atrium') {
        return null;
    }

    return (
      <div
        className={`fixed top-6 left-6 z-50 cursor-pointer group no-print flex items-center gap-3 ${getLogoColor()}`}
        onClick={goHome}
        title="Return to Home"
      >
          {brand.logo ? (
              <div className="w-10 h-10 rounded-full bg-white shadow-md p-0.5 flex items-center justify-center overflow-hidden group-hover:ring-2 group-hover:ring-gold/30 transition-all">
                  <img src={brand.logo} alt={brand.name} className="w-full h-full object-cover rounded-full" />
              </div>
          ) : (
              <AirplaneIcon className="w-8 h-8 group-hover:text-gold/80 transition-colors" />
          )}
          {brand.isWhiteLabel ? (
              <div className="flex flex-col">
                  <span className="font-display text-xl tracking-[0.15em] uppercase leading-none drop-shadow-md">{brand.name}</span>
                  {brand.tagline && <span className="font-serif text-[10px] italic tracking-[0.2em] mt-1 opacity-60">{brand.tagline}</span>}
              </div>
          ) : (
              <div className="flex flex-col">
                  <span className="font-display text-2xl tracking-[0.25em] uppercase leading-none drop-shadow-md">Aura</span>
                  <span className="font-serif text-[10px] italic tracking-[0.4em] mt-1 group-hover:text-gold/80 transition-colors">Traveler</span>
              </div>
          )}
      </div>
    );
  };

  if (step === 'dashboard') return <>{renderPastDueBanner()}{renderLogo()}<ErrorBoundary level="section"><Suspense fallback={<LoadingScreen />}><Dashboard onSelectTrip={handleDashboardSelect} onNewTrip={handleNewTrip} onBrowseTemplates={handleBrowseTemplates} user={user} onManageBilling={() => { setPaymentMode('manage'); setShowPayment(true); }} /></Suspense></ErrorBoundary>{renderGlobalControls()}<Suspense fallback={null}><WhiteLabelSettings isOpen={isWhiteLabelOpen} onClose={() => setIsWhiteLabelOpen(false)} userTier={user?.hasLicense ? 'pro' : 'free'} /></Suspense></>;

  if (step === 'templates') return <>{renderLogo()}<ErrorBoundary level="page"><Suspense fallback={<LoadingScreen />}><TemplateGallery onSelectTemplate={handleTemplateSelect} onBack={() => setStep('dashboard')} /></Suspense></ErrorBoundary>{renderGlobalControls()}</>;

  // If viewing a shared trip URL, show loading while loadPublicTrip fetches data
  if (step === 'onboarding' && urlTripId) return <LoadingScreen message="Loading itinerary..." />;
  if (step === 'loading' && urlTripId) return <LoadingScreen message="Loading itinerary..." />;
  if (step === 'onboarding') return <>{renderLogo()}<ErrorBoundary level="page"><Suspense fallback={<LoadingScreen />}><Onboarding onSubmit={handleOnboardingSubmit} initialData={userData || undefined} onBrowseTemplates={handleBrowseTemplates} userTier={user?.subscriptionTier} /></Suspense></ErrorBoundary>{renderGlobalControls()}<OnboardingTour isOpen={showOnboardingTour} onClose={() => setShowOnboardingTour(false)} /></>;
  if (step === 'selecting_dest' && userData) return <>{renderLogo()}<ErrorBoundary level="page"><Suspense fallback={<LoadingScreen />}><DestinationSelector userInput={userData} onSelect={handleDestinationSelected} onBack={() => setStep('onboarding')} /></Suspense></ErrorBoundary>{renderGlobalControls()}</>;
  if (step === 'atrium_loading') return <LoadingScreen message="Curating your signature collection..." />;
  if (step === 'atrium' && userData) return <>{renderLogo()}<ErrorBoundary level="page"><Suspense fallback={<LoadingScreen message="Preparing the Atrium..." />}><AtriumMode items={atriumItems} destination={userData.destination} onComplete={handleAtriumComplete} /></Suspense></ErrorBoundary>{renderGlobalControls()}</>;
  if (step === 'loading') return <LoadingScreen message={loadingMessage || "Architecting your bespoke itinerary..."} currentStep={loadingStep} destination={userData?.destination} />;

  // Voyage error: show clear message instead of blank page
  if (step === 'voyage' && voyageError && !tripData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-8 text-center bg-ivory min-h-screen">
        <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 text-gold">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
          </svg>
        </div>
        <h2 className="font-display text-3xl text-charcoal mb-4">
          This Itinerary Didn't Load
        </h2>
        <p className="font-serif text-lg text-charcoal/60 max-w-md mb-8">
          {voyageError}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => window.location.reload()} className="px-8 py-3 bg-gold text-white rounded-lg hover:bg-gold/90 transition font-sans tracking-wider uppercase text-sm">
            Try Again
          </button>
          <button onClick={() => navigate('/')} className="px-8 py-3 border border-charcoal/15 text-charcoal/60 rounded-lg hover:border-charcoal/30 transition font-sans tracking-wider uppercase text-sm">
            Create Fresh Itinerary
          </button>
        </div>
      </div>
    );
  }

  if (step === 'voyage' && userData && tripData) {
    return (
      <div className="min-h-screen bg-ivory text-charcoal pb-24 relative transition-colors duration-500">

        {/* Session Expiry Warning Banner */}
        {showSessionWarning && (
            <div className="fixed top-0 left-0 right-0 z-[200] bg-gradient-to-r from-amber-600 to-amber-500 text-white px-4 py-3 flex items-center justify-center gap-4 shadow-lg animate-fadeIn no-print">
                <span className="font-sans text-xs uppercase tracking-widest">Your session will expire soon.</span>
                <button
                    onClick={handleSessionRefresh}
                    className="bg-white text-amber-700 px-4 py-1.5 font-sans text-[10px] uppercase tracking-widest font-bold hover:bg-amber-50 transition-colors rounded-sm"
                >
                    Stay Logged In
                </button>
                <button
                    onClick={() => setShowSessionWarning(false)}
                    className="text-white/70 hover:text-white ml-2 text-lg leading-none"
                    aria-label="Dismiss"
                >
                    &times;
                </button>
            </div>
        )}

        {/* Auth Error Banner */}
        {authError && (
            <div className="fixed top-0 left-0 right-0 z-[200] bg-red-600 text-white px-4 py-3 flex items-center justify-center gap-4 shadow-lg animate-fadeIn no-print">
                <span className="font-sans text-xs tracking-wide">{authError}</span>
                <button
                    onClick={handleDismissAuthError}
                    className="text-white/70 hover:text-white ml-2 text-lg leading-none"
                    aria-label="Dismiss"
                >
                    &times;
                </button>
            </div>
        )}

        {/* Offline Indicator */}
        {!isAppOnline && (
            <div className="fixed bottom-4 left-4 z-[200] bg-charcoal/90 text-gold px-4 py-2 rounded-full shadow-xl font-sans text-[10px] uppercase tracking-widest flex items-center gap-2 animate-fadeIn no-print backdrop-blur-sm">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                Offline Mode
            </div>
        )}

        {/* PWA Install Banner */}
        {showInstallBanner && (
            <div className="fixed bottom-6 right-6 z-[100] bg-charcoal border border-gold/30 text-ivory px-6 py-4 rounded-sm shadow-2xl animate-fadeIn no-print max-w-xs">
                <div className="flex items-start gap-3">
                    <div className="flex-1">
                        <p className="font-display text-xs uppercase tracking-widest text-gold mb-1">Install Aura</p>
                        <p className="font-sans text-[11px] text-gray-300 leading-relaxed">Access your itineraries offline, even on flights without WiFi.</p>
                    </div>
                    <button
                        onClick={() => setShowInstallBanner(false)}
                        className="text-gray-500 hover:text-ivory text-lg leading-none mt-[-2px]"
                        aria-label="Dismiss"
                    >
                        &times;
                    </button>
                </div>
                <div className="flex gap-3 mt-3">
                    <button
                        onClick={async () => {
                            const accepted = await promptInstall();
                            if (accepted) setShowInstallBanner(false);
                        }}
                        className="flex-1 bg-gold text-charcoal px-4 py-2 font-sans text-[10px] uppercase tracking-widest font-bold hover:bg-gold/90 transition-colors"
                    >
                        Install App
                    </button>
                    <button
                        onClick={() => setShowInstallBanner(false)}
                        className="px-4 py-2 font-sans text-[10px] uppercase tracking-widest text-gray-400 hover:text-ivory transition-colors"
                    >
                        Not Now
                    </button>
                </div>
            </div>
        )}

        {/* Unsaved Trip Recovery Banner */}
        {showUnsavedTripBanner && unsavedTripBackup && (
            <div className="fixed top-0 left-0 right-0 z-[200] bg-gradient-to-r from-blue-700 to-blue-600 text-white px-4 py-3 flex flex-col sm:flex-row items-center justify-center gap-3 shadow-lg animate-fadeIn no-print">
                <span className="font-sans text-xs tracking-wide">
                    We found an unsaved itinerary from {new Date(unsavedTripBackup.savedAt).toLocaleDateString()}. Would you like to restore it?
                </span>
                <div className="flex gap-2">
                    <button
                        onClick={handleRestoreUnsavedTrip}
                        className="bg-white text-blue-700 px-4 py-1.5 font-sans text-[10px] uppercase tracking-widest font-bold hover:bg-blue-50 transition-colors rounded-sm"
                    >
                        Restore Itinerary
                    </button>
                    <button
                        onClick={handleDismissUnsavedTrip}
                        className="bg-white/20 text-white px-4 py-1.5 font-sans text-[10px] uppercase tracking-widest hover:bg-white/30 transition-colors rounded-sm"
                    >
                        Discard
                    </button>
                </div>
            </div>
        )}

        {/* Collaboration Banner */}
        {isCollaborativeView && (
            <div className="fixed top-0 left-0 right-0 z-[190] bg-gradient-to-r from-teal-700 to-teal-600 text-white px-4 py-2.5 flex items-center justify-center gap-3 shadow-lg animate-fadeIn no-print">
                <Users className="w-3.5 h-3.5 text-teal-200" />
                <span className="font-sans text-[11px] uppercase tracking-widest">
                    {collaborationRole === 'editor'
                      ? `Collaborating on this itinerary with ${tripOwnerName}`
                      : `Viewing this itinerary (shared by ${tripOwnerName})`
                    }
                </span>
                {collaborationRole === 'viewer' && (
                    <span className="bg-white/20 text-white text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full">
                        Read Only
                    </span>
                )}
            </div>
        )}

        {/* Collaborator Avatars */}
        {tripCollaborators.length > 0 && (
            <div className="fixed top-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 no-print" style={{ marginTop: isCollaborativeView ? '36px' : '0' }}>
                {/* Owner avatar */}
                <div className="w-8 h-8 rounded-full bg-gold/20 border-2 border-gold flex items-center justify-center" title={`Owner: ${tripOwnerName || 'You'}`}>
                    <span className="font-serif text-xs text-gold font-medium">
                        {(tripOwnerName || user?.name || 'O').charAt(0).toUpperCase()}
                    </span>
                </div>
                {/* Collaborator avatars */}
                {tripCollaborators.slice(0, 4).map((collab, i) => (
                    <div
                        key={collab.email}
                        className={`w-8 h-8 rounded-full flex items-center justify-center border-2 -ml-2 ${
                            collab.role === 'editor'
                                ? 'bg-teal-500/20 border-teal-400'
                                : 'bg-gray-500/20 border-gray-400'
                        }`}
                        title={`${collab.displayName || collab.email} (${collab.role})`}
                    >
                        <span className={`font-serif text-xs font-medium ${
                            collab.role === 'editor' ? 'text-teal-400' : 'text-gray-400'
                        }`}>
                            {(collab.displayName || collab.email).charAt(0).toUpperCase()}
                        </span>
                    </div>
                ))}
                {tripCollaborators.length > 4 && (
                    <div className="w-8 h-8 rounded-full bg-charcoal/80 border-2 border-gray-500 flex items-center justify-center -ml-2" title={`${tripCollaborators.length - 4} more`}>
                        <span className="font-sans text-[10px] text-gray-300">+{tripCollaborators.length - 4}</span>
                    </div>
                )}
            </div>
        )}

        {!showLanding && renderLogo()}
        {renderGlobalControls()}

        {isEditing && (
            <div className="fixed top-24 right-6 z-30 bg-gold text-charcoal px-4 py-2 text-[10px] uppercase tracking-widest font-bold animate-fadeIn shadow-lg">
                Edit Mode Active
            </div>
        )}

        <Suspense fallback={<div className="hidden"></div>}>
            <SharePreview ref={sharePreviewRef} metadata={userData} imageUrl={headerImage} summary={tripData.destinationSummary} />
        </Suspense>

        <main id="dossier-capture-root" role="main" className="bg-ivory">
          
          {/* PDF PAGE 1: COVER */}
          <div className={`pdf-section bg-ivory ${isExporting ? 'pb-0' : 'pb-12'}`}>
              <header className="w-full">
                 <Suspense fallback={<div className="w-full h-[100dvh] bg-charcoal animate-pulse" />}>
                   <Postcard destination={userData.destination} metadata={userData} imageUrl={headerImage} summary={tripData.destinationSummary} isExporting={isExporting} />
                 </Suspense>
              </header>
          </div>

          {/* PDF PAGE 1.5: WELCOME NOTE */}
          <div className="pdf-section welcome-note-section bg-ivory pb-8">
              <section className="max-w-3xl mx-auto px-8 py-8 text-center print:py-4 scroll-reveal">
                  <div className="mb-4">
                      <h3 className="font-sans text-[10px] uppercase tracking-ultra text-gold mb-2 welcome-note-label">A Personal Note</h3>
                      {isEditing ? (
                          <textarea 
                              className="w-full font-serif text-lg md:text-xl text-charcoal italic leading-relaxed text-center border-b border-gray-200 focus:border-gold outline-none bg-transparent min-h-[80px]"
                              value={tripData.welcomeNote}
                              onChange={(e) => handleWelcomeNoteUpdate(e.target.value)}
                              placeholder="Write a welcome note to your client..."
                          />
                      ) : (
                          <p className="font-serif text-lg text-charcoal italic leading-relaxed welcome-note-text">
                              "{tripData.welcomeNote || `Welcome to ${userData.destination}. Every detail has been curated to match your unique vibration.`}"
                          </p>
                      )}
                      {userData.agentFavorites && !isEditing && (
                          <p className="mt-2 font-sans text-xs text-gray-500 uppercase tracking-widest">
                              Curated by {userData.agencyName || (brand.isWhiteLabel ? brand.name : "Your Travel Designer")}
                          </p>
                      )}
                  </div>
              </section>
          </div>

          {!isExporting && <div className="voyage-divider" />}

          {/* PDF PAGE 2: LOGISTICS & PREP */}
          {(userData.includeLogistics || (isExporting && userData.includeBudget)) && (
            <div id="section-logistics" className={`pdf-section bg-ivory scroll-mt-16 ${!isExporting ? 'mb-6' : ''}`}>
                {userData.includeLogistics && (
                    <>
                    {/* Logistics toggle */}
                    {!isExporting && (
                      <button
                        onClick={() => setShowLogistics(!showLogistics)}
                        className="w-full flex items-center justify-between px-6 py-5 bg-gradient-to-r from-charcoal/5 to-gold/5 border border-gold/20 mb-4 hover:border-gold/40 hover:from-charcoal/8 hover:to-gold/10 transition-all rounded-lg group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center group-hover:bg-gold/25 transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gold"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" /></svg>
                          </div>
                          <span className="font-display text-sm uppercase tracking-[0.15em] text-charcoal">{showLogistics ? 'Flight & Hotel Details' : 'View Flight & Hotel Details'}</span>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={`w-5 h-5 text-gold transition-transform ${showLogistics ? 'rotate-180' : ''}`}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                        </svg>
                      </button>
                    )}
                    {(showLogistics || isExporting) && (
                    <section className="bg-sand/10 py-8 border-b border-sand print:py-4 print:break-inside-avoid">
                        <div className="text-center mb-8 print:mb-4">
                        <h2 className="font-display text-xl uppercase tracking-widest text-charcoal">Logistics & Recommendations</h2>
                        <div className="w-8 h-px bg-charcoal mx-auto mt-2"></div>
                        </div>
                        {tripData.logistics && (
                        <ErrorBoundary level="section">
                        <Suspense fallback={<div className="w-full h-48 bg-sand/10 animate-pulse flex items-center justify-center"><div className="w-6 h-6 border-2 border-gold/40 border-t-gold rounded-full animate-spin"></div></div>}>
                            <LogisticsModule
                                data={tripData.logistics}
                                destination={userData.destination}
                                visualAesthetic={userData.visualAesthetic}
                                onUpdate={handleLogisticsUpdate}
                                isEditing={isEditing}
                                onLocationFocus={handleLocationFocus}
                            />
                        </Suspense>
                        </ErrorBoundary>
                        )}
                    </section>
                    )}
                    </>
                )}

                {/* BOOKING SUMMARY removed — lock feature disabled */}

                {/* Export-only Sidebar content for linear layout */}
                {isExporting && userData.includeBudget && tripData.weather && (
                    <div className="px-12 py-8 bg-ivory">
                        <ErrorBoundary level="section">
                        <AtmosphericSidebar
                            weather={tripData.weather}
                            packingList={tripData.packingList || []}
                            budget={tripData.budget}
                            itinerary={tripData.itinerary}
                        />
                        </ErrorBoundary>
                    </div>
                )}
            </div>
          )}

          {!isExporting && <div className="voyage-divider" />}

          {/* PDF PAGES 3+: ITINERARY DAYS */}
          <section id="section-itinerary" className="w-full max-w-[1400px] mx-auto px-6 py-12 relative print:py-4 print:max-w-none print:px-0 scroll-mt-16 scroll-reveal">
            {/* Header visible only on web view since PDF splits days */}
            {!isExporting && (
                <div className="text-center mb-12 print:mb-6">
                  <h2 className="font-display fluid-title uppercase tracking-widest mb-3 text-charcoal">The Itinerary</h2>
                  <div className="w-16 h-1 bg-gold mx-auto mb-8"></div>
                  
                  {timeSaved && (
                      <div className="flex justify-center mb-8">
                          <div className="bg-gold/10 border border-gold/30 px-6 py-3 rounded-full flex items-center gap-3 animate-fadeIn">
                              <Clock className="w-4 h-4 text-gold" />
                              <span className="font-sans text-[10px] uppercase tracking-widest text-gold font-bold">
                                  Aura Saved You {timeSaved.toFixed(1)} Hours of Planning
                              </span>
                          </div>
                      </div>
                  )}

                  {/* Day navigation — full-width hero images stacked */}
                  {/* Day navigation — compact thumbnail strip */}
            {tripData.itinerary?.length > 1 && (
              <div className="mb-10">
                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-charcoal/30 text-center mb-4">Your Journey at a Glance</p>
                <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory justify-center px-4">
                  {(tripData.itinerary || []).map((day, idx) => (
                    <button
                      key={day.day}
                      onClick={() => document.getElementById(`day-${day.day}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="flex-shrink-0 w-[180px] rounded-xl overflow-hidden bg-charcoal relative group cursor-pointer press-haptic hover:shadow-lg transition-all snap-start"
                    >
                      <div className="aspect-[4/3] w-full relative overflow-hidden">
                        {day.imageUrl ? (
                          <>
                            <img src={day.imageUrl} alt={day.moodTitle || `Day ${day.day}`} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/30 to-transparent" />
                          </>
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-charcoal to-charcoal/80" />
                        )}
                        <div className="absolute inset-0 flex items-end p-3">
                          <div>
                            <span className="font-display text-2xl font-bold text-gold/70 leading-none">{String(day.day).padStart(2, '0')}</span>
                            <p className="font-serif italic text-xs text-ivory mt-0.5 leading-tight line-clamp-1">{day.moodTitle}</p>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
                </div>
            )}
            
            <div className={isExporting ? "block" : "w-full"}>
                <div className={isExporting ? "w-full" : "w-full relative print:w-full"}>
                  {(!tripData.itinerary || tripData.itinerary.length === 0) && !isExporting && (
                    isPublicView ? (
                      (tripData as any)?._isStale ? (
                        /* Stale trip — generation failed or pre-fix data. Show CTA instead of spinner */
                        <div className="flex flex-col items-center justify-center min-h-[60vh] px-8 text-center">
                          <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-10 h-10 text-gold">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                            </svg>
                          </div>
                          <h2 className="font-display text-3xl text-charcoal mb-4">
                            This Itinerary Didn't Complete
                          </h2>
                          <p className="font-serif text-lg text-charcoal/60 max-w-md mb-8">
                            The original generation was interrupted. Let's create a fresh one — it only takes 90 seconds.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <button
                              onClick={() => {
                                if (userData?.destination) {
                                  setStep('onboarding');
                                  setSavedTripId(null);
                                  setIsPublicView(false);
                                  setHeaderImage(undefined);
                                } else {
                                  handleNewTrip(true);
                                }
                              }}
                              className="px-8 py-3 bg-gold text-white rounded-lg hover:bg-gold/90 transition font-sans tracking-wider uppercase text-sm"
                            >
                              Create {userData?.destination ? `${userData.destination} Itinerary` : 'Fresh Itinerary'}
                            </button>
                            <a
                              href="/"
                              className="px-8 py-3 border border-charcoal/15 text-charcoal/60 rounded-lg hover:border-charcoal/30 transition font-sans tracking-wider uppercase text-sm text-center"
                            >
                              Browse Destinations
                            </a>
                          </div>
                        </div>
                      ) : (
                      /* Active trip — still generating. Show progress spinner */
                      <div className="flex flex-col items-center justify-center py-16 space-y-6 max-w-md mx-auto">
                        <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gold/40"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>
                        </div>
                        <div className="text-center">
                          <p className="font-display text-xl text-charcoal mb-2" style={{ fontWeight: 300 }}>Itinerary In Progress</p>
                          <p className="font-sans text-sm text-charcoal/50 mb-6">The day-by-day details are still being generated. This usually takes 30-60 seconds after the trip is created.</p>
                        </div>
                        {/* Pipeline status indicators */}
                        <div className="w-full space-y-3">
                          {[
                            { label: 'Trip overview', done: !!tripData.destinationSummary },
                            { label: 'Welcome note', done: !!tripData.welcomeNote },
                            { label: 'Logistics & flights', done: !!tripData.logistics },
                            { label: 'Daily itinerary', done: false },
                            { label: 'Venue images', done: false },
                          ].map((stage, i) => (
                            <div key={i} className="flex items-center gap-3">
                              {stage.done ? (
                                <svg className="w-5 h-5 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                              ) : (
                                <div className="w-5 h-5 border-2 border-gold/30 border-t-gold rounded-full animate-spin shrink-0" />
                              )}
                              <span className={`font-sans text-sm ${stage.done ? 'text-charcoal/70' : 'text-charcoal/40'}`}>{stage.label}</span>
                            </div>
                          ))}
                        </div>
                        <p className="font-sans text-[10px] uppercase tracking-widest text-charcoal/25 pt-4">Auto-refreshing every 5 seconds</p>
                      </div>
                      )
                    ) : (
                      <div className="flex flex-col items-center justify-center py-24 space-y-6 animate-pulse">
                        <div className="w-12 h-12 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
                        <p className="font-serif italic text-gray-600 tracking-widest text-sm">Synthesizing your daily experiences...</p>
                      </div>
                    )
                  )}
                  {/* Smart Itinerary Navigation */}
                  {tripData && tripData.itinerary?.length > 0 && !isExporting && (
                    <div className="sticky top-0 z-40 liquid-glass-light rounded-none py-2.5 sm:py-3 px-3 sm:px-4 -mx-4 mb-6">
                      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto scroll-hide max-w-5xl mx-auto items-center">
                        {/* Section shortcuts */}
                        {userData.includeLogistics && (
                          <button
                            onClick={() => document.getElementById('section-logistics')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                            className="shrink-0 px-3 sm:px-4 py-2 font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.15em] bg-charcoal/5 border border-charcoal/10 hover:border-gold hover:text-gold text-charcoal/60 transition-all min-h-[36px] sm:min-h-[40px] whitespace-nowrap rounded-full flex items-center gap-1.5"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"/></svg>
                            <span className="hidden sm:inline">Logistics</span>
                          </button>
                        )}

                        {/* Divider */}
                        {userData.includeLogistics && <div className="w-px h-5 bg-sand/50 shrink-0 mx-0.5" />}

                        {/* Day pills */}
                        {(tripData.itinerary || []).map(day => (
                          <button
                            key={day.day}
                            onClick={() => {
                              document.getElementById(`day-${day.day}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                            className="shrink-0 px-3 sm:px-4 py-2 font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.15em] border border-charcoal/15 hover:border-gold hover:text-gold hover:bg-gold/5 transition-all min-h-[36px] sm:min-h-[40px] whitespace-nowrap rounded-full flex items-center gap-1.5"
                          >
                            <span className="font-display text-sm sm:text-base font-bold leading-none text-gold/70">{String(day.day).padStart(2, '0')}</span>
                            <span className="hidden md:inline text-charcoal/50 max-w-[100px] truncate">{day.moodTitle}</span>
                            <span className="md:hidden">Day {day.day}</span>
                          </button>
                        ))}

                        {/* Divider */}
                        <div className="w-px h-5 bg-sand/50 shrink-0 mx-0.5" />

                        {/* Essentials shortcut */}
                        <button
                          onClick={() => document.getElementById('section-essentials')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                          className="shrink-0 px-3 sm:px-4 py-2 font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.15em] bg-charcoal/5 border border-charcoal/10 hover:border-gold hover:text-gold text-charcoal/60 transition-all min-h-[36px] sm:min-h-[40px] whitespace-nowrap rounded-full flex items-center gap-1.5 press-haptic"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z"/></svg>
                          <span className="hidden sm:inline">Essentials</span>
                        </button>

                        {/* Studio shortcut — opens Aura Studio modal directly */}
                        <button
                          onClick={() => setIsAuraStudioOpen(true)}
                          className="shrink-0 px-3 sm:px-4 py-2 font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.15em] bg-charcoal/5 border border-charcoal/10 hover:border-gold hover:text-gold text-charcoal/60 transition-all min-h-[36px] sm:min-h-[40px] whitespace-nowrap rounded-full flex items-center gap-1.5 press-haptic"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"/></svg>
                          <span className="hidden sm:inline">Studio</span>
                        </button>


                        {/* Share shortcut */}
                        <button
                          onClick={() => document.getElementById('section-share')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                          className="shrink-0 px-3 sm:px-4 py-2 font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.15em] bg-charcoal/5 border border-charcoal/10 hover:border-gold hover:text-gold text-charcoal/60 transition-all min-h-[36px] sm:min-h-[40px] whitespace-nowrap rounded-full flex items-center gap-1.5 press-haptic"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z"/></svg>
                          <span className="hidden sm:inline">Share</span>
                        </button>
                      </div>
                    </div>
                  )}

                  <Suspense fallback={<div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-gold/40 border-t-gold rounded-full animate-spin"></div></div>}>
                    {isExporting ? (
                      // Each day gets its own pdf-section for clean page breaks
                      (tripData.itinerary || []).map(day => (
                        <div key={day.day} className="pdf-section bg-ivory">
                            <div className="p-4">
                                <ErrorBoundary level="section">
                                 <DailyItinerary
                                    day={day}
                                    metadata={userData}
                                    onUpdate={handleDayUpdate}
                                    isEditing={isEditing}
                                    isExporting={isExporting}
                                    onLocationFocus={handleLocationFocus}
                                  />
                                </ErrorBoundary>
                            </div>
                        </div>
                      ))
                    ) : (
                      <div>
                              {(tripData.itinerary || []).map((day, index) => (
                                    <div
                                      key={`day-${index}-${day.day}`}
                                      className={`pdf-section bg-ivory p-4 mb-8 relative dnd-day-card scroll-mt-16 scroll-reveal voyage-card`}
                                      id={`day-${day.day}`}
                                    >
                                        <ErrorBoundary level="section">
                                          <DailyItinerary
                                            day={day}
                                            metadata={userData}
                                            onUpdate={handleDayUpdate}
                                            isEditing={isEditing}
                                            isExporting={isExporting}
                                            isReorderMode={isReorderMode}
                                            onLocationFocus={handleLocationFocus}
                                          />
                                        </ErrorBoundary>
                                    </div>
                              ))}
                      </div>
                    )}
                  </Suspense>

                  {/* PDF PAGE LAST: APPENDIX & FOOTER */}
                  <div className="pdf-section bg-ivory p-4">
                      {tripData.digitalAppendix && tripData.digitalAppendix.length > 0 && (
                          <div className="mt-16 mb-8 pt-8 border-t border-dashed border-gray-300 no-print">
                              <h3 className="font-display text-xl uppercase tracking-widest text-charcoal mb-6">Digital Appendix</h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {tripData.digitalAppendix.map((item) => (
                                      <div key={item.id} className="p-4 bg-gray-50 border border-sand flex justify-between items-start opacity-70 hover:opacity-100 transition-opacity">
                                          <div>
                                              <h4 className="font-display text-sm text-charcoal mb-1">{item.title}</h4>
                                              <p className="font-serif text-xs text-gray-600 italic line-clamp-2">{item.sensoryDescription}</p>
                                          </div>
                                          <span className="font-sans text-[10px] uppercase tracking-widest text-gold shrink-0 border border-gold/30 px-1 py-0.5 rounded-sm">Score: {item.auraScore}</span>
                                      </div>
                                  ))}
                              </div>
                          </div>
                      )}

                      {/* Export-only branded closing section */}
                      {isExporting && (
                          <div className="pdf-section bg-ivory text-center py-24 px-16">
                              <div className="max-w-xl mx-auto">
                                  <div className="w-12 h-px mx-auto mb-8" style={{ backgroundColor: brand.primaryColor }}></div>
                                  {brand.logo && (
                                      <div className="w-16 h-16 rounded-full bg-white shadow-md p-0.5 mx-auto mb-6 flex items-center justify-center overflow-hidden">
                                          <img src={brand.logo} alt={brand.name} className="w-full h-full object-cover rounded-full" />
                                      </div>
                                  )}
                                  <p className="font-sans text-[10px] uppercase tracking-[0.3em] mb-6" style={{ color: brand.primaryColor }}>Prepared with precision by</p>
                                  <h3 className="font-display text-2xl uppercase tracking-[0.2em] text-charcoal mb-4">
                                      {userData.agencyName || brand.name}
                                  </h3>
                                  {userData.agentName && (
                                      <p className="font-serif text-sm text-charcoal/90 italic mb-1">{userData.agentName}</p>
                                  )}
                                  {userData.agentTitle && (
                                      <p className="font-sans text-[10px] uppercase tracking-widest text-charcoal/90 mb-4">{userData.agentTitle}</p>
                                  )}
                                  {(userData.agentEmail || userData.agentPhone || brand.contactEmail || brand.contactPhone) && (
                                      <div className="mt-4 space-y-1">
                                          {(userData.agentEmail || brand.contactEmail) && <p className="font-sans text-[10px] text-charcoal/80">{userData.agentEmail || brand.contactEmail}</p>}
                                          {(userData.agentPhone || brand.contactPhone) && <p className="font-sans text-[10px] text-charcoal/80">{userData.agentPhone || brand.contactPhone}</p>}
                                      </div>
                                  )}
                                  <div className="w-12 h-px mx-auto mt-8 mb-6" style={{ backgroundColor: brand.primaryColor }}></div>
                                  <p className="font-serif text-[10px] text-charcoal/80 italic leading-relaxed">
                                      {brand.footerText}. All booking links and details should be verified before finalizing reservations.
                                  </p>
                                  <p className="font-sans text-[10px] uppercase tracking-[0.2em] mt-6" style={{ color: brand.primaryColor, opacity: 0.6 }}>{brand.website}</p>
                              </div>
                          </div>
                      )}

                      {/* Preservation Protocol section removed — actions moved to dropdown menu */}
                  </div>
                </div>
            </div>
          </section>

          {/* Highlights section removed — redundant with carousel navigation */}

          {/* --- LOCAL HAPPENINGS — real events during the travel dates --- */}
          {tripData?.localEvents && tripData.localEvents.length > 0 && !isExporting && (
            <section id="local-happenings" className="w-full max-w-4xl mx-auto px-6 py-16 no-print scroll-mt-16">
              <Suspense fallback={<div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-gold/40 border-t-gold rounded-full animate-spin" /></div>}>
                <LocalEvents events={tripData.localEvents} destination={userData?.destination} />
              </Suspense>
            </section>
          )}

          {/* --- ASK AURA — floating concierge popup (fixed-position) --- */}
          {tripData && !isExporting && savedTripId && (
            <Suspense fallback={null}>
              <AskAura itineraryId={savedTripId} />
            </Suspense>
          )}

          {/* EXPEDITION ESSENTIALS — Map + Weather/Packing/Budget */}
          {!isExporting && (
            <section id="section-essentials" className="w-full max-w-[1400px] mx-auto px-6 py-16 no-print scroll-mt-16">
              <div className="text-center mb-12">
                <h2 className="font-display text-2xl md:text-3xl uppercase tracking-widest mb-3 text-charcoal">Expedition Essentials</h2>
                <div className="w-16 h-1 bg-gold mx-auto"></div>
              </div>
              {/* Map — full width on desktop */}
              <div className="mb-8">
                <ErrorBoundary level="section">
                  <Suspense fallback={<div className="w-full h-[400px] rounded-sm border border-sand/30 bg-sand/10 animate-pulse flex items-center justify-center"><div className="w-6 h-6 border-2 border-gold/40 border-t-gold rounded-full animate-spin"></div></div>}>
                    <InteractiveMap
                      logistics={tripData.logistics}
                      itinerary={tripData.itinerary}
                      focus={activeLocation}
                      className="w-full h-[400px] lg:h-[500px] rounded-sm border border-sand shadow-sm"
                    />
                  </Suspense>
                </ErrorBoundary>
              </div>
              {/* Trip Intel: Weather, Packing, Budget — below the map */}
              <div>
                <ErrorBoundary level="section">
                  <Suspense fallback={<div className="w-full h-48 bg-sand/10 animate-pulse flex items-center justify-center"><div className="w-6 h-6 border-2 border-gold/40 border-t-gold rounded-full animate-spin"></div></div>}>
                    <AtmosphericSidebar
                      weather={tripData.weather || { temperatureRange: 'Check locally', condition: 'Data pending', description: `Check local weather for ${userData?.destination || 'your destination'}`, location: userData?.destination || '' } as WeatherData}
                      packingList={tripData.packingList || []}
                      budget={userData.includeBudget ? tripData.budget : undefined}
                      itinerary={tripData.itinerary}
                    />
                  </Suspense>
                </ErrorBoundary>
              </div>

              {/* eSIM — compact card for international trips */}
              {tripData.eSimRecommendation && tripData.isInternational && (
                <div className="mt-10">
                  <Suspense fallback={null}>
                    <ESimModule recommendation={tripData.eSimRecommendation} />
                  </Suspense>
                </div>
              )}

            </section>
          )}

          {/* EMAIL CAPTURE — Post-generation lead capture */}
          {!isExporting && isPublicView && (
            <section className="w-full max-w-3xl mx-auto px-6 py-16 no-print text-center">
              <div className="liquid-glass-light border border-gold/10 p-8 md:p-12">
                <h3 className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/70 mb-4">Stay Connected</h3>
                <p className="font-serif text-lg md:text-xl text-charcoal/80 italic mb-6">
                  Love this {userData?.destination} itinerary? Get a link sent to your inbox — plus insider tips for your trip.
                </p>
                <form
                  onSubmit={async (e: React.FormEvent<HTMLFormElement>) => {
                    e.preventDefault();
                    const form = e.currentTarget as HTMLFormElement;
                    const emailInput = form.querySelector('input[type="email"]') as HTMLInputElement | null;
                    const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
                    if (!emailInput?.value || !btn) return;
                    const email = emailInput.value.toLowerCase().trim();
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
                    btn.textContent = 'Sending...';
                    btn.disabled = true;
                    try {
                      // Capture email to Firestore for analytics
                      const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
                      await addDoc(collection(db, 'email_captures'), {
                        email,
                        source: 'voyage_view',
                        destination: userData?.destination || '',
                        tripId: savedTripId || '',
                        tripUrl: window.location.href,
                        capturedAt: serverTimestamp(),
                      });
                      // Actually send the trip link via email (public endpoint, no auth required)
                      const tripUrl = `${window.location.origin}/trip/${savedTripId}`;
                      await fetch('/api/send-trip-link', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          recipientEmail: email,
                          destination: userData?.destination || '',
                          tripUrl,
                        }),
                      });
                      btn.textContent = 'Sent!';
                      emailInput.value = '';
                      setTimeout(() => { btn.textContent = 'Send My Trip'; btn.disabled = false; }, 3000);
                    } catch {
                      btn.textContent = 'Send My Trip';
                      btn.disabled = false;
                    }
                  }}
                  className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                >
                  <input
                    type="email"
                    placeholder="your@email.com"
                    required
                    className="flex-1 px-4 py-3 border border-charcoal/15 bg-white font-serif text-sm text-charcoal placeholder:text-charcoal/30 outline-none focus:border-gold transition-colors"
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gold text-charcoal font-sans text-[11px] uppercase tracking-[0.12em] font-bold hover:bg-charcoal hover:text-ivory transition-all min-h-[44px] shrink-0"
                  >
                    Send My Trip
                  </button>
                </form>
                <p className="font-sans text-[10px] text-charcoal/25 mt-4 uppercase tracking-[0.2em]">No spam. Just your itinerary link.</p>
              </div>
            </section>
          )}

          {/* Itinerary Footer — Terms, Disclaimer, Engine Version */}
          {tripData && !isExporting && (
            <footer className="text-center py-8 px-6 font-sans text-xs uppercase tracking-widest text-gray-500 no-print border-t border-gray-100 mt-12 max-w-4xl mx-auto">
              <Suspense fallback={<div className="w-full h-12 bg-sand/10 animate-pulse"></div>}>
                  <BusinessCard data={userData} />
              </Suspense>
              {(userData.licenseNumber || userData.contactInfo) && (
                  <div className="mb-6 space-y-2">
                    {userData.licenseNumber && <p className="text-gray-600">License: {userData.licenseNumber}</p>}
                    {userData.contactInfo && <p className="text-gray-600">{userData.contactInfo}</p>}
                    <div className="w-8 h-px bg-gold mx-auto my-4"></div>
                  </div>
              )}
              <div className="max-w-2xl mx-auto mb-4">
                  {isEditing ? (
                      <textarea
                        className="w-full h-16 text-center bg-transparent border border-gray-200 p-2 focus:border-gold outline-none text-xs leading-relaxed text-gray-600"
                        value={tripData.termsAndConditions}
                        onChange={(e) => handleTermsUpdate(e.target.value)}
                        placeholder="Enter Terms & Conditions..."
                      />
                  ) : (
                      <p className="text-[10px] text-gray-600 leading-relaxed opacity-80">{tripData.termsAndConditions}</p>
                  )}
              </div>
              <p className="mb-4">{brand.isWhiteLabel ? `Powered by ${brand.name}` : 'Aura Traveler Engine v3.1'} &bull; 2026</p>
              <div className="flex justify-center gap-6 text-[10px] text-gray-600">
                  <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">Terms of Service</a>
                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">Privacy Policy</a>
              </div>
            </footer>
          )}
        </main>

        <Suspense fallback={<div className="hidden"></div>}>
            <SocialMemento isOpen={isMementoOpen} onClose={() => setIsMementoOpen(false)} metadata={userData} imageUrl={headerImage} onConsumeCredit={consumeCredit} />
            <EmailModal isOpen={isEmailModalOpen} onClose={() => setIsEmailModalOpen(false)} onSubmit={handleEmailDispatch} destination={userData.destination} />
            {savedTripId && (
              <SharePrivacyModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                tripId={savedTripId}
                shareUrl={`${window.location.origin}/voyage/${savedTripId}`}
                isPublic={shareIsPublic}
                destination={userData.destination}
                onVisibilityChange={handleShareVisibilityChange}
              />
            )}
            <VeoStudio isOpen={isVeoStudioOpen} onClose={() => setIsVeoStudioOpen(false)} metadata={userData} tripImages={tripImages} onConsumeCredit={consumeCredit} />
            {/* Floating Aura Studio preview button */}
            {step === 'voyage' && !isAuraStudioOpen && !isExporting && (
              <button
                onClick={() => setIsAuraStudioOpen(true)}
                className="fixed bottom-24 right-6 z-30 flex items-center gap-2 px-4 py-2.5 bg-charcoal/90 backdrop-blur-sm border border-gold/30 rounded-full shadow-lg shadow-gold/10 hover:border-gold/60 hover:shadow-gold/20 transition-all duration-300 group no-print"
                aria-label="Open Aura Studio"
              >
                <Sparkles className="w-4 h-4 text-gold group-hover:scale-110 transition-transform" />
                <span className="font-display text-[10px] tracking-[0.15em] uppercase text-ivory/80 group-hover:text-ivory transition-colors">4 Templates Ready</span>
              </button>
            )}
            <AuraStudio isOpen={isAuraStudioOpen} onClose={() => setIsAuraStudioOpen(false)} metadata={userData} tripData={tripData} headerImage={headerImage} tripImages={tripImages} onConsumeCredit={consumeCredit} />
            <WhiteLabelSettings isOpen={isWhiteLabelOpen} onClose={() => setIsWhiteLabelOpen(false)} userTier={user?.hasLicense ? 'pro' : 'free'} />
        </Suspense>

        {/* Elastic scroll progress indicator */}
        {!isExporting && (
          <div
            className="scroll-progress-bar no-print"
            ref={scrollProgressRef}
          />
        )}

        {/* Now Playing Activity Bar — mobile: full action bar / desktop: compact */}
        {!isExporting && !isEditing && (tripData.itinerary || []).length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 z-30 liquid-glass-dark rounded-none no-print safe-area-bottom">
            {/* Desktop: compact info bar */}
            <div className="hidden sm:flex max-w-5xl mx-auto px-6 py-3 items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gold/15 flex items-center justify-center shrink-0">
                <span className="font-display text-sm font-bold text-gold">{(tripData.itinerary || []).length}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-white/40 mb-0.5">{userData.destination}</p>
                <p className="font-serif text-sm text-ivory truncate">{(tripData.itinerary || []).length} days curated</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setIsEmailModalOpen(true)} className="px-4 py-2 font-sans text-[10px] uppercase tracking-[0.15em] text-gold border border-gold/30 rounded-full hover:bg-gold hover:text-charcoal transition-all min-h-[36px] press-haptic">
                  Dispatch
                </button>
              </div>
            </div>

            {/* Mobile: full action bar with all key actions */}
            <div className="sm:hidden px-4 py-3">
              <div className="flex items-center justify-around gap-1">
                <button aria-label="Save to vault" onClick={() => saveToVault(tripData, userData)} disabled={isSaving} className="flex flex-col items-center gap-1 min-w-[56px] min-h-[44px] justify-center press-haptic">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gold"><path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"/></svg>
                  <span className="font-sans text-[10px] uppercase tracking-wider text-ivory/50">{isSaving ? 'Saving...' : 'Save to Vault'}</span>
                </button>
                <button aria-label="Send itinerary via email" onClick={() => setIsEmailModalOpen(true)} className="flex flex-col items-center gap-1 min-w-[56px] min-h-[44px] justify-center press-haptic">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-ivory/60"><path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"/></svg>
                  <span className="font-sans text-[10px] uppercase tracking-wider text-ivory/50">Dispatch</span>
                </button>
                <div className="relative">
                  <button aria-label="Export to calendar" onClick={() => setCalendarMenuOpen(!calendarMenuOpen)} className="flex flex-col items-center gap-1 min-w-[56px] min-h-[44px] justify-center press-haptic">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-ivory/60"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/></svg>
                    <span className="font-sans text-[10px] uppercase tracking-wider text-ivory/50">Calendar</span>
                  </button>
                  {calendarMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setCalendarMenuOpen(false)} />
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-charcoal/95 backdrop-blur-sm rounded-lg shadow-xl border border-gold/20 overflow-hidden z-50 min-w-[180px]">
                        <button onClick={() => { generateGoogleCalendarExport(tripData, userData); setCalendarMenuOpen(false); }} className="w-full text-left px-4 py-3 text-[10px] font-sans uppercase tracking-widest text-ivory hover:bg-gold/20 transition-colors flex items-center gap-3">
                          <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none"><path d="M18 3h-1V1h-2v2H9V1H7v2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2z" fill="#4285F4"/><path d="M6 9h12v12H6z" fill="#fff"/><path d="M10.5 17.5l-2.25-2.25 1.06-1.06 1.19 1.19 3.19-3.19 1.06 1.06L10.5 17.5z" fill="#34A853"/></svg>
                          Google Calendar
                        </button>
                        <button onClick={() => { generateCalendarExport(tripData, userData); setCalendarMenuOpen(false); }} className="w-full text-left px-4 py-3 text-[10px] font-sans uppercase tracking-widest text-ivory hover:bg-gold/20 transition-colors flex items-center gap-3 border-t border-ivory/10">
                          <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" fill="#FF3B30"/><path d="M3 9h18v10a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" fill="#fff"/><rect x="6.75" y="1" width="1.5" height="5" rx=".75" fill="#FF3B30"/><rect x="15.75" y="1" width="1.5" height="5" rx=".75" fill="#FF3B30"/></svg>
                          Apple Calendar
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <button aria-label="Share itinerary" onClick={shareLink} disabled={isSharing} className="flex flex-col items-center gap-1 min-w-[56px] min-h-[44px] justify-center press-haptic">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-ivory/60"><path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z"/></svg>
                  <span className="font-sans text-[10px] uppercase tracking-wider text-ivory/50">Share</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Share Link Toast/Notification — only for authenticated trip owners, not public viewers */}
        {savedTripId && !isPublicView && user && (
            <div className="fixed bottom-24 right-6 z-40 no-print animate-slideUp">
                <div className="bg-charcoal/90 backdrop-blur-md border border-gold/30 p-4 rounded-sm shadow-2xl flex flex-col gap-2 min-w-[240px]">
                    <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-gold/60">Shareable Itinerary Link</p>
                    <div className="flex items-center gap-2">
                        <input
                            readOnly
                            value={`${window.location.origin}/voyage/${savedTripId}`}
                            className="bg-black/40 border border-white/10 text-ivory text-[10px] px-3 py-2 rounded-sm w-full font-mono outline-none focus:border-gold/50 transition-colors"
                        />
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/voyage/${savedTripId}`);
                                const btn = document.getElementById('copy-btn');
                                if (btn) btn.innerText = 'COPIED';
                                setTimeout(() => { if(btn) btn.innerText = 'COPY'; }, 2000);
                            }}
                            id="copy-btn"
                            className="bg-gold text-charcoal px-3 py-2 text-[10px] font-sans font-bold uppercase tracking-widest hover:bg-white transition-colors"
                        >
                            COPY
                        </button>
                    </div>
                </div>
            </div>
        )}
        
        {/* Debug Info removed — Gemini key no longer bundled into the client.
            See /api/ai/* server proxy for status surfacing if needed. */}

        {/* Mobile Bottom Navigation Bar */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-ivory/95 backdrop-blur-md border-t border-sand/40 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] no-print" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            <div className="flex items-center justify-around px-2 py-2">
                <button
                    onClick={() => {
                      if (isEditing) {
                        if (!window.confirm('You have unsaved edits. Leave without saving?')) return;
                        setIsEditing(false);
                      }
                      setStep('dashboard');
                    }}
                    className="flex flex-col items-center gap-0.5 px-3 py-2 min-w-[64px] min-h-[48px] justify-center"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-charcoal/70"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" /></svg>
                    <span className="font-sans text-[10px] uppercase tracking-wider text-charcoal/70">Vault</span>
                </button>
                <button
                    onClick={() => handleNewTrip()}
                    className="flex flex-col items-center gap-0.5 px-3 py-2 min-w-[64px] min-h-[48px] justify-center"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gold"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                    <span className="font-sans text-[10px] uppercase tracking-wider text-gold font-bold">New Trip</span>
                </button>
                {!isEditing && collaborationRole !== 'viewer' && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex flex-col items-center gap-0.5 px-3 py-2 min-w-[64px] min-h-[48px] justify-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-charcoal/70"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" /></svg>
                        <span className="font-sans text-[10px] uppercase tracking-wider text-charcoal/70">Edit</span>
                    </button>
                )}
                {isEditing && (
                    <button
                        onClick={saveChanges}
                        disabled={isSaving}
                        className="flex flex-col items-center gap-0.5 px-3 py-2 min-w-[64px] min-h-[48px] justify-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-emerald-600"><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
                        <span className="font-sans text-[10px] uppercase tracking-wider text-emerald-600 font-bold">{isSaving ? 'Saving' : 'Save'}</span>
                    </button>
                )}
                <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex flex-col items-center gap-0.5 px-3 py-2 min-w-[64px] min-h-[48px] justify-center"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-charcoal/70"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
                    <span className="font-sans text-[10px] uppercase tracking-wider text-charcoal/70">Menu</span>
                </button>
            </div>
        </nav>
      </div>
    );
  }

  // Fallback for any unhandled step — never return null (causes blank screen)
  return <LoadingScreen message="Preparing your experience..." />;
};

const PageFallback = () => {
    // Reset body background for marketing pages (overrides the landing page dark flash fix)
    React.useEffect(() => {
      document.body.style.backgroundColor = '';
    }, []);
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
          <div className="w-8 h-8 border border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
};

/** Scroll to top on every route change */
const ScrollToTopOnNavigate: React.FC = () => {
    const { pathname } = useLocation();
    React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
};

const App: React.FC = () => {
    return (
        <ErrorBoundary level="page">
        <ToastProvider>
        <BrandProvider>
        <>
            <ScrollToTopOnNavigate />
            <Suspense fallback={<PageFallback />}>
            <Routes>
                {/* Public trip share */}
                <Route path="/trip/:tripId" element={<MainApp />} />
                {/* In-app voyage view */}
                <Route path="/voyage" element={<MainApp />} />
                <Route path="/voyage/:tripId" element={<MainApp />} />
                <Route path="/field-notes" element={<Suspense fallback={<LoadingScreen message="Loading Field Notes..." />}><Journeys /></Suspense>} />
                <Route path="/field-notes/:slug" element={<Suspense fallback={<LoadingScreen message="Loading..." />}><JourneyPost /></Suspense>} />
                {/* Marketing pages */}
                <Route path="/philosophy" element={<Suspense fallback={<PageFallback />}><PhilosophyPage /></Suspense>} />
                {/* /pricing is the canonical Kobu pricing page; /membership kept as an alias (canonical tag dedupes) */}
                <Route path="/pricing" element={<Suspense fallback={<PageFallback />}><PricingPage /></Suspense>} />
                <Route path="/membership" element={<Suspense fallback={<PageFallback />}><PricingPage /></Suspense>} />
                <Route path="/create-itinerary" element={<Navigate to="/create" replace />} />
                <Route path="/for-business" element={<Navigate to="/for-agencies" replace />} />
                <Route path="/for-agencies" element={<Suspense fallback={<PageFallback />}><ForAgenciesPage /></Suspense>} />
                <Route path="/for-hotels" element={<Suspense fallback={<PageFallback />}><ForHotelsPage /></Suspense>} />
                <Route path="/hotel-concierge" element={<Suspense fallback={<PageFallback />}><HotelConciergePage /></Suspense>} />
                <Route path="/faq" element={<Suspense fallback={<PageFallback />}><FaqPage /></Suspense>} />
                <Route path="/journal" element={<Suspense fallback={<PageFallback />}><JournalPage /></Suspense>} />
                <Route path="/journal/:slug" element={<Suspense fallback={<PageFallback />}><JournalPostPage /></Suspense>} />
                <Route path="/about" element={<Suspense fallback={<LoadingScreen message="Loading..." />}><AboutPage /></Suspense>} />
                <Route path="/contact" element={<Suspense fallback={<LoadingScreen message="Loading..." />}><ContactPage /></Suspense>} />
                <Route path="/day-planner" element={<Suspense fallback={<PageFallback />}><DayPlannerPage /></Suspense>} />
                <Route path="/day-plan/:planId" element={<Suspense fallback={<PageFallback />}><DayPlannerPage /></Suspense>} />
                <Route path="/destinations" element={<Suspense fallback={<PageFallback />}><DestinationsIndexPage /></Suspense>} />
                <Route path="/destinations/:slug" element={<Suspense fallback={<PageFallback />}><DestinationGuidePage /></Suspense>} />
                <Route path="/explore" element={<Suspense fallback={<PageFallback />}><ExplorePage /></Suspense>} />
                <Route path="/concierge" element={<Navigate to="/pricing" replace />} />
                {/* Vault — direct access to saved itineraries */}
                <Route path="/vault" element={<MainApp />} />
                {/* Redirects for legacy/convenience paths */}
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/create" element={<MainApp />} />
                <Route path="/app" element={<Navigate to="/" replace />} />
                {/* Main app (landing page / authenticated dashboard) */}
                <Route path="/" element={<MainApp />} />
                {/* 404 for unknown routes */}
                <Route path="*" element={<Suspense fallback={<LoadingScreen message="Loading..." />}><NotFoundPage /></Suspense>} />
            </Routes>
            </Suspense>
        </>
        </BrandProvider>
        </ToastProvider>
        </ErrorBoundary>
    );
};

export default App;

